import {
  BBox,
  Vec2,
  arcSweep,
  booleanEntities,
  cutPointsFor,
  deg2rad,
  distancePointSegment,
  entityBBox,
  entityPaths,
  entityQuadrants,
  angleInArc,
  intersectEntities,
  LINETYPES,
  makeCircle,
  mergeEntities,
  makeDimension,
  makeLine,
  makePolyline,
  makeRectangle,
  makeText,
  mirrorEntityAcross,
  namePieces,
  normalizeAngle,
  offsetEntity,
  rotateEntity,
  scaleEntity,
  sliceAtIntersections,
  splitEntityAt,
  tangentCentre,
  translateEntity,
  withHandlePoint,
  withNewId,
  type BooleanMode,
  type Entity,
  type EntityRole,
  type EntityStyle,
} from '@lakaran/geometry';

/**
 * Procedural modelling graph — the pure half of the node editor.
 *
 * Geometry is authored as points: every primitive carries a **reference** (its snapping
 * point) plus an origin, and exposes its vertices, centres and midlines as pickable
 * references. Steps never destroy geometry — a trim only *hides* edges and keeps the list,
 * so any operation can be undone by re-ticking it. The node list is the modelling history
 * and is saved with the project.
 *
 * No DOM and no editor session live here: the LK node editor is the other half.
 */

export type NodeType =
  | 'rect'
  | 'circle'
  | 'line'
  | 'bezier'
  | 'translate'
  | 'rotate'
  | 'scale'
  | 'mirror'
  | 'array'
  | 'offset'
  | 'trim'
  | 'extend'
  | 'slice'
  | 'merge'
  | 'boolean'
  | 'coincident'
  | 'tangent'
  | 'constraint'
  | 'number'
  | 'measure'
  | 'add'
  | 'subtract'
  | 'multiply'
  | 'divide'
  | 'text'
  | 'dimension'
  | 'emit';

/** Palette grouping: primitives, modelling steps, constraints, annotations, maths, and the sink. */
export type NodeCategory =
  | 'geometry'
  | 'tools'
  | 'constraint'
  | 'annotation'
  | 'math'
  | 'output';

export const NODE_CATEGORY_ORDER: readonly NodeCategory[] = [
  'geometry',
  'tools',
  'constraint',
  'annotation',
  'math',
  'output',
];

export const NODE_CATEGORY_LABELS: Record<NodeCategory, string> = {
  geometry: 'Geometry',
  tools: 'Tools',
  constraint: 'Constraints',
  annotation: 'Annotation',
  math: 'Math',
  output: 'Output',
};

export interface NodeCategoryGroup {
  category: NodeCategory;
  label: string;
  nodes: NodeDef[];
}

/** The palette, grouped for a dropdown/search list. */
export function nodesByCategory(): NodeCategoryGroup[] {
  return NODE_CATEGORY_ORDER.map((category) => ({
    category,
    label: NODE_CATEGORY_LABELS[category],
    nodes: Object.values(NODE_DEFS).filter((def) => def.category === category && !def.hidden),
  }));
}

export interface GraphNode {
  id: string;
  type: NodeType;
  x: number;
  y: number;
  params: Record<string, number>;
  /** Text content for the annotation steps (the number-only graph keeps it out of `params`). */
  text?: string;
  /** Reference picks, keyed by the field declared in `NodeDef.refs`. */
  refs?: Record<string, string>;
  /**
   * Numeric inputs driven by a maths step instead of a typed number:
   * param id → value-node id. The typed number stays as the fallback.
   */
  values?: Record<string, string>;
  /**
   * Non-destructive trim: keys of the edges the user removed. Nothing is destroyed —
   * clearing the list restores every span.
   */
  hidden?: string[];
  /**
   * Pieces picked in the drawing instead of taken from the incoming stream (the mirror step's
   * manual selection), as stream keys. Empty means "use the previous shape".
   */
  picks?: string[];
}

/**
 * `from` produces entities, `to` consumes them. `slot` names which input the wire feeds: absent is
 * the step's own stream input, `'a'` / `'b'` are the sockets a step declares (merge's A and B).
 */
export interface GraphEdge {
  from: string;
  to: string;
  slot?: string;
}

export interface NodeGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface NodeParamChoice {
  value: number;
  label: string;
}

export interface NodeParamDef {
  id: string;
  label: string;
  value: number;
  unit: 'mm' | 'deg' | 'num';
  min?: number;
  /** Step for the numeric field (line weight uses quarter pixels). */
  step?: number;
  /** Renders a dropdown instead of a numeric field. */
  choices?: readonly NodeParamChoice[];
  /** Only shown/applied when the node's `mode` param equals one of these values. */
  modes?: number[];
}

/** A free-text field on a step (the text step's content). */
export interface NodeTextField {
  id: string;
  label: string;
  value: string;
}

/** A pickable point/edge of another step's output. */
export interface NodeRefDef {
  id: string;
  label: string;
  /** Restricts what the picker offers. Defaults to every point and midline. */
  kinds?: RefKind[];
  /** Only shown/applied when the node's `mode` param equals one of these values. */
  modes?: number[];
}

export interface NodeDef {
  type: NodeType;
  title: string;
  description: string;
  /** True when the node consumes an upstream entity stream. */
  consumes: boolean;
  /** True for maths steps: they produce a number, never geometry. */
  value?: boolean;
  params: NodeParamDef[];
  /** Free-text field, when the step carries one (text content). */
  text?: NodeTextField;
  /** Reference pickers (origin, vertex, centre or midline of another step). */
  refs?: NodeRefDef[];
  /** Renders the produced edges as a keep/remove list (non-destructive trim). */
  listsEdges?: boolean;
  /** Renders the input stream as a pick list, chosen in the drawing (mirror's selection). */
  listsPicks?: boolean;
  /** Renders a pick list of pieces that are left out of the step's operation (merge). */
  listsExcludes?: boolean;
  /** Kept for older graphs but no longer offered in the palette. */
  hidden?: boolean;
  /** Named input sockets on the card, in order — merge declares A and B on its input edge. */
  inputs?: NodeInputDef[];
  /** Palette grouping. */
  category: NodeCategory;
}

/** One declared input socket: `slot` is stored on the wire, `label` is what the card shows. */
export interface NodeInputDef {
  slot: string;
  label: string;
}

/** The input sockets a step declares, in card order (empty for the ordinary single input). */
export function nodeInputs(node: GraphNode): NodeInputDef[] {
  return NODE_DEFS[node.type]?.inputs ?? [];
}

const PARAM = {
  ox: (value: number): NodeParamDef => ({ id: 'ox', label: 'Origin X', value, unit: 'mm' }),
  oy: (value: number): NodeParamDef => ({ id: 'oy', label: 'Origin Y', value, unit: 'mm' }),
  width: (value: number): NodeParamDef => ({ id: 'width', label: 'Width', value, unit: 'mm', min: 0.001 }),
  height: (value: number): NodeParamDef => ({ id: 'height', label: 'Height', value, unit: 'mm' }),
  radius: (value: number): NodeParamDef => ({ id: 'radius', label: 'Radius', value, unit: 'mm', min: 0.001 }),
  length: (value: number): NodeParamDef => ({ id: 'length', label: 'Length', value, unit: 'mm', min: 0.001 }),
  angle: (value: number): NodeParamDef => ({ id: 'angle', label: 'Angle', value, unit: 'deg' }),
  dx: (value: number): NodeParamDef => ({ id: 'dx', label: 'ΔX', value, unit: 'mm' }),
  dy: (value: number): NodeParamDef => ({ id: 'dy', label: 'ΔY', value, unit: 'mm' }),
  distance: (value: number): NodeParamDef => ({ id: 'distance', label: 'Distance', value, unit: 'mm' }),
  count: (value: number): NodeParamDef => ({ id: 'count', label: 'Count', value, unit: 'num', min: 1 }),
};

/** Line weight is in screen pixels, like a CAD lineweight preview. */
export const NODE_LINETYPE_CHOICES: readonly NodeParamChoice[] = LINETYPES.map((type, index) => ({
  value: index,
  label: type.label,
}));

/** A short palette instead of a colour dialog: 0 keeps the layer colour. */
export const NODE_COLOUR_CHOICES: readonly NodeParamChoice[] = [
  { value: 0, label: 'Layer colour' },
  { value: 0x1f2937, label: 'Ink' },
  { value: 0x6b7280, label: 'Grey' },
  { value: 0xb91c1c, label: 'Red' },
  { value: 0xc2410c, label: 'Orange' },
  { value: 0xb45309, label: 'Amber' },
  { value: 0x15803d, label: 'Green' },
  { value: 0x0f766e, label: 'Teal' },
  { value: 0x1d4ed8, label: 'Blue' },
  { value: 0x7e22ce, label: 'Purple' },
  { value: 0xbe185d, label: 'Magenta' },
];

/** Whether a primitive is real geometry or a construction line the operators must ignore. */
export const NODE_ROLE_CHOICES: readonly NodeParamChoice[] = [
  { value: 0, label: 'Geometry' },
  { value: 1, label: 'Construction' },
];

/** Every primitive carries it: a plate, a centre line and a reference outline are all "a line". */
export const ROLE_PARAM: NodeParamDef = {
  id: 'role',
  label: 'Role',
  value: 0,
  unit: 'num',
  choices: NODE_ROLE_CHOICES,
};

/** A construction line reads as one in the drawing: muted, whatever the layer. */
const AID_COLOUR = 0x8a94a6;

/** The role a primitive is set to, or `undefined` when it is ordinary geometry. */
export function roleOf(v: (id: string, fallback: number) => number): EntityRole | undefined {
  // Anything but the first choice is a construction line, so graphs saved with a second aid kind
  // keep working.
  return Math.round(v('role', 0)) >= 1 ? 'construction' : undefined;
}

/**
 * A construction line is drawn muted (unless the step chose a colour itself) and dashed (unless it
 * chose a pattern). Linetypes are carried on the entity as data — the viewport preview draws solid
 * strokes — so the colour is what makes a construction line obvious, and the pattern is what an
 * export writes out.
 */
export function roleStyle(
  style: EntityStyle,
  role: EntityRole | undefined,
): EntityStyle & { role?: EntityRole } {
  if (!role) return style;
  const chosen = style.linetype && style.linetype !== 'continuous';
  return {
    color: AID_COLOUR,
    ...style,
    role,
    linetype: chosen ? style.linetype : 'dashed',
  };
}

/** A level of detail is the chord error a welded run may carry — how round a sliced arc stays. */
export const DETAIL_PARAM: NodeParamDef = {
  id: 'detail',
  label: 'Detail',
  value: 0.01,
  unit: 'mm',
  min: 0.001,
  step: 0.001,
};

/** Style overrides every geometry step carries, so line weight, pattern and colour are per step. */
const STYLE_PARAMS: readonly NodeParamDef[] = [
  {
    id: 'lineweight',
    label: 'Line weight',
    value: 1,
    unit: 'num',
    min: 0,
    step: 0.25,
  },
  { id: 'linetype', label: 'Line type', value: 0, unit: 'num', choices: NODE_LINETYPE_CHOICES },
  { id: 'color', label: 'Colour', value: 0, unit: 'num', choices: NODE_COLOUR_CHOICES },
];

/** The style a step puts on the geometry it creates; unset entries fall back to the layer. */
export function nodeStyle(v: (id: string, fallback: number) => number): EntityStyle {
  const style: EntityStyle = {};
  const weight = v('lineweight', 0);
  if (weight > 0) style.lineWidth = weight;
  const linetype = LINETYPES[Math.round(v('linetype', 0))]?.id;
  if (linetype) style.linetype = linetype;
  const color = Math.round(v('color', 0));
  if (color > 0) style.color = color;
  return style;
}

/** Array modes — one node, three behaviours. */
export const ARRAY_MODES: readonly NodeParamChoice[] = [
  { value: 0, label: 'Linear' },
  { value: 1, label: 'Circular' },
  { value: 2, label: 'Path' },
];

export type ArrayMode = 'linear' | 'circular' | 'path';

const ARRAY_MODE_ORDER: readonly ArrayMode[] = ['linear', 'circular', 'path'];

/** The array node's current mode. Graphs saved before modes existed read as linear. */
export function arrayModeOf(node: GraphNode): ArrayMode {
  const raw = node.params['mode'];
  const index = Number.isFinite(raw) ? Math.round(raw as number) : 0;
  return ARRAY_MODE_ORDER[index] ?? 'linear';
}

/** Line modes — one node, four ways of placing the far end. */
export const LINE_MODES: readonly NodeParamChoice[] = [
  { value: 0, label: 'Length + angle' },
  { value: 1, label: 'To point' },
  { value: 2, label: 'Coordinates' },
  { value: 3, label: 'Tangent' },
];

export type LineMode = 'length' | 'point' | 'coordinate' | 'tangent';

const LINE_MODE_ORDER: readonly LineMode[] = ['length', 'point', 'coordinate', 'tangent'];

/** The line node's current mode. Graphs saved before modes existed read as length + angle. */
export function lineModeOf(node: GraphNode): LineMode {
  const raw = node.params['mode'];
  const index = Number.isFinite(raw) ? Math.round(raw as number) : 0;
  return LINE_MODE_ORDER[index] ?? 'length';
}

function modeValue(node: GraphNode): number {
  // The older combined constraint switched its rows on `kind`; Coincident and Tangent are their
  // own steps and need no switch.
  const raw = node.type === 'constraint' ? node.params['kind'] : node.params['mode'];
  return Number.isFinite(raw) ? Math.round(raw as number) : 0;
}

/** What a Measure step reads off a shape. */
export const MEASURE_MODES: readonly NodeParamChoice[] = [
  { value: 0, label: 'Width' },
  { value: 1, label: 'Height' },
  { value: 2, label: 'Length' },
  { value: 3, label: 'Radius' },
  { value: 4, label: 'X' },
  { value: 5, label: 'Y' },
  { value: 6, label: 'Count' },
];

export type MeasureMode = 'width' | 'height' | 'length' | 'radius' | 'x' | 'y' | 'count';

const MEASURE_ORDER: readonly MeasureMode[] = ['width', 'height', 'length', 'radius', 'x', 'y', 'count'];

/** The measure node's current reading. Graphs saved before it existed read as width. */
export function measureModeOf(node: GraphNode): MeasureMode {
  const raw = node.params['what'];
  const index = Number.isFinite(raw) ? Math.round(raw as number) : 0;
  return MEASURE_ORDER[index] ?? 'width';
}

/** One shape measured the way a Measure step asks — its size, its spine, or its position. */
export function measureEntity(entity: Entity, mode: MeasureMode): number {
  const box = entityBBox(entity);
  switch (mode) {
    case 'width':
      return box.width;
    case 'height':
      return box.height;
    case 'x':
      return box.center.x;
    case 'y':
      return box.center.y;
    case 'radius':
      if (entity.type === 'circle' || entity.type === 'arc') return entity.radius;
      if (entity.type === 'ellipse') return Math.max(entity.rx, entity.ry);
      return Math.max(box.width, box.height) / 2;
    case 'length': {
      if (entity.type === 'line') return entity.a.distanceTo(entity.b);
      if (entity.type === 'circle') return 2 * Math.PI * entity.radius;
      if (entity.type === 'arc') {
        return entity.radius * arcSweep(entity.startAngle, entity.endAngle, entity.ccw);
      }
      let total = 0;
      const points = pathPoints(entity);
      for (let index = 1; index < points.length; index++) {
        total += points[index - 1]!.distanceTo(points[index]!);
      }
      return total;
    }
    default:
      return 0;
  }
}

/** Params that apply in the node's current mode (everything, for mode-less nodes). */
export function activeParams(node: GraphNode): NodeParamDef[] {
  const mode = modeValue(node);
  return NODE_DEFS[node.type].params.filter(
    (param) => !param.modes || param.modes.includes(mode),
  );
}

/** Reference pickers that apply in the node's current mode. */
export function activeRefs(node: GraphNode): NodeRefDef[] {
  const mode = modeValue(node);
  return (NODE_DEFS[node.type].refs ?? []).filter(
    (ref) => !ref.modes || ref.modes.includes(mode),
  );
}

export const NODE_DEFS: Record<NodeType, NodeDef> = {
  rect: {
    type: 'rect',
    title: 'Rectangle',
    description: 'Plate from its reference or origin, added to the incoming stream',
    consumes: true,
    params: [PARAM.ox(0), PARAM.oy(0), PARAM.width(60), PARAM.height(40), ROLE_PARAM, ...STYLE_PARAMS],
    refs: [{ id: 'reference', label: 'Reference' }],
    category: 'geometry',
  },
  circle: {
    type: 'circle',
    title: 'Circle',
    description: 'Circle at its reference or origin, added to the incoming stream',
    consumes: true,
    params: [PARAM.ox(0), PARAM.oy(0), PARAM.radius(12), ROLE_PARAM, ...STYLE_PARAMS],
    refs: [{ id: 'reference', label: 'Reference' }],
    category: 'geometry',
  },
  line: {
    type: 'line',
    title: 'Line',
    description: 'Line from its start to a length + angle, to a picked point, or to typed coordinates',
    consumes: true,
    params: [
      { id: 'mode', label: 'Mode', value: 0, unit: 'num', choices: LINE_MODES },
      PARAM.ox(0),
      PARAM.oy(0),
      { ...PARAM.length(50), modes: [0] },
      { ...PARAM.angle(0), modes: [0] },
      { id: 'endX', label: 'End X', value: 50, unit: 'mm', modes: [2] },
      { id: 'endY', label: 'End Y', value: 0, unit: 'mm', modes: [2] },
      {
        id: 'side',
        label: 'Tangent side',
        value: 0,
        unit: 'num',
        choices: [
          { value: 0, label: 'Left' },
          { value: 1, label: 'Right' },
        ],
        modes: [3],
      },
      ROLE_PARAM,
      ...STYLE_PARAMS,
    ],
    refs: [
      { id: 'reference', label: 'Reference' },
      // "To point": the far end is a pick, so the line follows whatever it is pinned to.
      { id: 'target', label: 'End point', modes: [1] },
      // "Tangent": the far end is where the line touches a circle or arc.
      { id: 'tangent', label: 'Tangent to', kinds: ['path'], modes: [3] },
    ],
    category: 'geometry',
  },
  bezier: {
    type: 'bezier',
    title: 'Bézier',
    description: 'Cubic curve from four control points, added to the incoming stream',
    consumes: true,
    params: [
      { id: 'x1', label: 'P1 X', value: 0, unit: 'mm' },
      { id: 'y1', label: 'P1 Y', value: 0, unit: 'mm' },
      { id: 'x2', label: 'P2 X', value: 20, unit: 'mm' },
      { id: 'y2', label: 'P2 Y', value: 40, unit: 'mm' },
      { id: 'x3', label: 'P3 X', value: 60, unit: 'mm' },
      { id: 'y3', label: 'P3 Y', value: 40, unit: 'mm' },
      { id: 'x4', label: 'P4 X', value: 80, unit: 'mm' },
      { id: 'y4', label: 'P4 Y', value: 0, unit: 'mm' },
      ROLE_PARAM,
      ...STYLE_PARAMS,
    ],
    category: 'geometry',
  },
  text: {
    type: 'text',
    title: 'Text',
    description: 'Single-line annotation at its origin, added to the incoming stream',
    consumes: true,
    params: [
      PARAM.ox(0),
      PARAM.oy(0),
      { id: 'height', label: 'Text height', value: 3.5, unit: 'mm', min: 0.1 },
      PARAM.angle(0),
      ...STYLE_PARAMS,
    ],
    text: { id: 'content', label: 'Content', value: 'TEXT' },
    refs: [{ id: 'reference', label: 'Reference' }],
    category: 'annotation',
  },
  dimension: {
    type: 'dimension',
    title: 'Dimension',
    description: 'Linear or aligned dimension between two points — picked or typed',
    consumes: true,
    params: [
      {
        id: 'kind',
        label: 'Kind',
        value: 0,
        unit: 'num',
        choices: [
          { value: 0, label: 'Linear' },
          { value: 1, label: 'Aligned' },
        ],
      },
      { id: 'x1', label: 'From X', value: 0, unit: 'mm' },
      { id: 'y1', label: 'From Y', value: 0, unit: 'mm' },
      { id: 'x2', label: 'To X', value: 60, unit: 'mm' },
      { id: 'y2', label: 'To Y', value: 0, unit: 'mm' },
      { id: 'offset', label: 'Offset', value: 10, unit: 'mm' },
      { id: 'height', label: 'Text height', value: 3.5, unit: 'mm', min: 0.1 },
      { id: 'decimals', label: 'Decimals', value: 3, unit: 'num', min: 0, step: 1 },
      ...STYLE_PARAMS,
    ],
    // Both ends are picks first: a dimension between two points of the model follows them.
    refs: [
      { id: 'from', label: 'From' },
      { id: 'to', label: 'To' },
    ],
    category: 'annotation',
  },
  translate: {
    type: 'translate',
    title: 'Move',
    description: 'Shift the stream, or move it onto a picked point',
    consumes: true,
    params: [PARAM.dx(0), PARAM.dy(0)],
    refs: [{ id: 'to', label: 'Move to' }],
    category: 'tools',
  },
  rotate: {
    type: 'rotate',
    title: 'Rotate',
    description: 'Rotate about the origin or a picked point',
    consumes: true,
    params: [PARAM.angle(45)],
    refs: [{ id: 'center', label: 'About' }],
    category: 'tools',
  },
  scale: {
    type: 'scale',
    title: 'Scale',
    description: 'Scale about the origin or a picked point',
    consumes: true,
    params: [{ id: 'factor', label: 'Factor', value: 2, unit: 'num' }],
    refs: [{ id: 'center', label: 'About' }],
    category: 'tools',
  },
  mirror: {
    type: 'mirror',
    title: 'Mirror',
    description: 'Reflect the previous shape, or only the pieces picked in the drawing, across an axis',
    consumes: true,
    params: [
      {
        id: 'source',
        label: 'Reflect',
        value: 0,
        unit: 'num',
        choices: [
          { value: 0, label: 'Previous shape' },
          { value: 1, label: 'Picked pieces' },
        ],
      },
      { ...PARAM.angle(90), label: 'Axis angle' },
    ],
    refs: [
      // Axis or edge only: a pick names geometry, so the axis is a real line in the drawing.
      { id: 'axis', label: 'Mirror axis', kinds: ['midline', 'path'] },
    ],
    listsPicks: true,
    category: 'tools',
  },
  array: {
    type: 'array',
    title: 'Array',
    description: 'Repeat the stream — linear, circular, or along a path',
    consumes: true,
    params: [
      { id: 'mode', label: 'Mode', value: 0, unit: 'num', choices: ARRAY_MODES },
      PARAM.count(3),
      { ...PARAM.dx(80), modes: [0] },
      { ...PARAM.dy(0), modes: [0] },
      { ...PARAM.angle(30), label: 'Step angle', modes: [1] },
      {
        id: 'align',
        label: 'Align to path',
        value: 1,
        unit: 'num',
        choices: [
          { value: 0, label: 'No' },
          { value: 1, label: 'Yes' },
        ],
        modes: [2],
      },
      { ...PARAM.ox(0), label: 'Origin X', modes: [2] },
      { ...PARAM.oy(0), label: 'Origin Y', modes: [2] },
    ],
    refs: [
      { id: 'center', label: 'About', modes: [1] },
      { id: 'path', label: 'Path', kinds: ['path'], modes: [2] },
      { id: 'reference', label: 'Transform origin', modes: [2] },
    ],
    category: 'tools',
  },
  offset: {
    type: 'offset',
    title: 'Offset',
    description: 'Offset each entity by a distance',
    consumes: true,
    params: [PARAM.distance(5)],
    category: 'tools',
  },
  trim: {
    type: 'trim',
    title: 'Trim',
    description: 'Click pieces in the drawing to cut them away',
    consumes: true,
    params: [],
    listsEdges: true,
    category: 'tools',
  },
  extend: {
    type: 'extend',
    title: 'Extend',
    description: 'Grow each end to the next edge in the stream',
    consumes: true,
    params: [],
    category: 'tools',
  },
  slice: {
    type: 'slice',
    title: 'Slice',
    description: 'Split at intersections into separate objects',
    consumes: true,
    params: [],
    refs: [{ id: 'against', label: 'Against' }],
    category: 'tools',
  },
  merge: {
    type: 'merge',
    title: 'Merge',
    description: 'Join A and B into one outline — everything that touches is welded together',
    consumes: true,
    inputs: [
      { slot: 'a', label: 'A' },
      { slot: 'b', label: 'B' },
    ],
    listsExcludes: true,
    params: [{ id: 'gap', label: 'Gap', value: 0.05, unit: 'mm', min: 0, step: 0.01 }, DETAIL_PARAM],
    category: 'tools',
  },
  boolean: {
    type: 'boolean',
    title: 'Boolean',
    description: 'Intersect, union or inverse-intersect A and B — lines are sliced at the crossings',
    consumes: true,
    inputs: [
      { slot: 'a', label: 'A' },
      { slot: 'b', label: 'B' },
    ],
    listsExcludes: true,
    params: [
      {
        id: 'mode',
        label: 'Mode',
        value: 1,
        unit: 'num',
        choices: [
          { value: 0, label: 'Intersect' },
          { value: 1, label: 'Union' },
          { value: 2, label: 'Inverse' },
        ],
      },
      { id: 'gap', label: 'Gap', value: 0.05, unit: 'mm', min: 0, step: 0.01 },
      DETAIL_PARAM,
    ],
    category: 'tools',
  },
  coincident: {
    type: 'coincident',
    title: 'Coincident',
    description: 'Pull the point A belongs to onto point B — two picks and nothing else',
    consumes: true,
    params: [],
    refs: [
      { id: 'a', label: 'A · moves' },
      { id: 'b', label: 'B · target' },
    ],
    category: 'constraint',
  },
  tangent: {
    type: 'tangent',
    title: 'Tangent',
    description:
      'The circle wired in is moved until it touches the target edge — one node, one tangent point',
    consumes: true,
    params: [],
    // Tangency names the object it must touch, not a point on it.
    refs: [{ id: 'target', label: 'Tangent to', kinds: ['entity'] }],
    category: 'constraint',
  },
  /**
   * Older graphs carry one `constraint` step with a `kind` parameter. It still evaluates, but it is
   * no longer in the palette — Coincident and Tangent are two steps now.
   */
  constraint: {
    type: 'constraint',
    title: 'Constraint',
    description: 'Older combined step — use Coincident or Tangent',
    consumes: true,
    hidden: true,
    params: [
      {
        id: 'kind',
        label: 'Kind',
        value: 0,
        unit: 'num',
        choices: [
          { value: 0, label: 'Coincident' },
          { value: 1, label: 'Tangent' },
        ],
      },
    ],
    refs: [
      { id: 'a', label: 'A · moves', modes: [0] },
      { id: 'b', label: 'B · target', modes: [0] },
      { id: 'target', label: 'Tangent to', kinds: ['path'], modes: [1] },
    ],
    category: 'constraint',
  },
  number: {
    type: 'number',
    title: 'Number',
    description: 'A constant value other steps can use',
    consumes: false,
    value: true,
    params: [{ id: 'value', label: 'Value', value: 10, unit: 'num' }],
    category: 'math',
  },
  measure: {
    type: 'measure',
    title: 'Measure',
    description: 'A number read off a shape — its width, height, length, radius or position',
    consumes: false,
    value: true,
    params: [{ id: 'what', label: 'Measure', value: 0, unit: 'num', choices: MEASURE_MODES }],
    refs: [{ id: 'of', label: 'Of', kinds: ['path'] }],
    category: 'math',
  },
  add: {
    type: 'add',
    title: 'Add',
    description: 'A + B',
    consumes: false,
    value: true,
    params: [
      { id: 'a', label: 'A', value: 0, unit: 'num' },
      { id: 'b', label: 'B', value: 0, unit: 'num' },
    ],
    category: 'math',
  },
  subtract: {
    type: 'subtract',
    title: 'Subtract',
    description: 'A − B',
    consumes: false,
    value: true,
    params: [
      { id: 'a', label: 'A', value: 0, unit: 'num' },
      { id: 'b', label: 'B', value: 0, unit: 'num' },
    ],
    category: 'math',
  },
  multiply: {
    type: 'multiply',
    title: 'Multiply',
    description: 'A × B',
    consumes: false,
    value: true,
    params: [
      { id: 'a', label: 'A', value: 1, unit: 'num' },
      { id: 'b', label: 'B', value: 1, unit: 'num' },
    ],
    category: 'math',
  },
  divide: {
    type: 'divide',
    title: 'Divide',
    description: 'A ÷ B (0 gives 0)',
    consumes: false,
    value: true,
    params: [
      { id: 'a', label: 'A', value: 1, unit: 'num' },
      { id: 'b', label: 'B', value: 2, unit: 'num' },
    ],
    category: 'math',
  },
  emit: {
    type: 'emit',
    title: 'Output',
    description: 'Procedural result sent to the drawing',
    consumes: true,
    params: [],
    category: 'output',
  },
};

export const OUTPUT_NODE_TYPE: NodeType = 'emit';

/** Short prefix used for result names: `R3`, `C1`, `T2.1`. */
const NAME_PREFIX: Record<NodeType, string> = {
  rect: 'R',
  circle: 'C',
  line: 'L',
  bezier: 'B',
  text: 'T',
  dimension: 'D',
  translate: 'M',
  rotate: 'T',
  scale: 'D',
  mirror: 'I',
  array: 'A',
  offset: 'O',
  trim: 'X',
  extend: 'E',
  slice: 'S',
  merge: 'G',
  boolean: 'U',
  // Coincident and Tangent are two steps; the letter says which demand it is.
  coincident: 'K',
  tangent: 'K',
  constraint: 'K',
  number: 'N',
  measure: 'V',
  add: '+',
  subtract: '−',
  multiply: '×',
  divide: '÷',
  emit: 'P',
};

let nodeCounter = 0;

export function newNodeId(): string {
  nodeCounter += 1;
  return `n${nodeCounter.toString(36)}`;
}

/**
 * Raises the id counter past every id already in `graph`. Without this, a graph loaded from a
 * file (ids `n1…n9`) would hand a new step an id that already belongs to another step, and
 * selection, deletion and evaluation would all act on the wrong node.
 */
export function reserveNodeIds(graph: NodeGraph): void {
  for (const node of graph.nodes) {
    const match = /^n([0-9a-z]+)$/.exec(node.id);
    if (!match) continue;
    const value = Number.parseInt(match[1]!, 36);
    if (Number.isFinite(value) && value > nodeCounter) nodeCounter = value;
  }
}

export function createNode(type: NodeType, x: number, y: number): GraphNode {
  const def = NODE_DEFS[type];
  const params: Record<string, number> = {};
  for (const param of def.params) params[param.id] = param.value;
  return { id: newNodeId(), type, x, y, params };
}

export function emptyGraph(): NodeGraph {
  return { nodes: [], edges: [] };
}

/** A blank project's graph: nothing but the sink, ready for the first step. */
export function blankGraph(): NodeGraph {
  return { nodes: [createNode(OUTPUT_NODE_TYPE, 32, 24)], edges: [] };
}

export function findOutputNode(graph: NodeGraph): GraphNode | undefined {
  return graph.nodes.find((node) => node.type === OUTPUT_NODE_TYPE);
}

/** The steps wired into `nodeId`, in connection order — more than one only for merge. */
export function upstreamsOf(graph: NodeGraph, nodeId: string): GraphNode[] {
  const out: GraphNode[] = [];
  for (const edge of graph.edges) {
    if (edge.to !== nodeId) continue;
    const node = graph.nodes.find((candidate) => candidate.id === edge.from);
    if (node) out.push(node);
  }
  return out;
}

/** The step wired into one input of `nodeId`: its stream input, or the socket named `slot`. */
export function upstreamOf(
  graph: NodeGraph,
  nodeId: string,
  slot = '',
): GraphNode | undefined {
  const edge = graph.edges.find(
    (candidate) => candidate.to === nodeId && (candidate.slot ?? '') === slot,
  );
  if (!edge) return undefined;
  return graph.nodes.find((node) => node.id === edge.from);
}

/** The step wired after `nodeId`, if any. Streams have a single input, so there is at most one. */
export function downstreamOf(graph: NodeGraph, nodeId: string): GraphNode | undefined {
  const edge = graph.edges.find((candidate) => candidate.from === nodeId);
  if (!edge) return undefined;
  return graph.nodes.find((node) => node.id === edge.to);
}

/** Every step reachable downstream of `nodeId` (never includes the step itself). */
export function descendantsOf(graph: NodeGraph, nodeId: string): Set<string> {
  const out = new Set<string>();
  const seen = new Set<string>([nodeId]);
  let current = downstreamOf(graph, nodeId);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    out.add(current.id);
    current = downstreamOf(graph, current.id);
  }
  return out;
}

// ----------------------------------------------------------------- references

export type RefKind =
  | 'origin'
  | 'vertex'
  | 'center'
  | 'quadrant'
  | 'midline'
  | 'path'
  | 'intersection'
  /** A whole object — the primitive itself, for constraints that touch it anywhere. */
  | 'entity';

export interface NodeRef {
  nodeId: string;
  kind: RefKind;
  index: number;
}

/** Legacy kind name kept so older graphs keep loading. */
const KIND_ALIASES: Record<string, RefKind> = {
  origin: 'origin',
  vertex: 'vertex',
  center: 'center',
  centre: 'center',
  quadrant: 'quadrant',
  quad: 'quadrant',
  midline: 'midline',
  edge: 'midline',
  path: 'path',
  intersection: 'intersection',
  int: 'intersection',
  entity: 'entity',
  object: 'entity',
};

export const REF_KIND_LABELS: Record<RefKind, string> = {
  origin: 'Origin',
  vertex: 'Vertex',
  center: 'Centre',
  quadrant: 'Quadrant',
  midline: 'Midline',
  path: 'Path',
  intersection: 'Intersection',
  entity: 'Object',
};

export function serializeRef(ref: NodeRef): string {
  return `${ref.nodeId}:${ref.kind}:${ref.index}`;
}

export function parseRef(text: string | undefined): NodeRef | null {
  if (!text) return null;
  const [nodeId, rawKind, index] = text.split(':');
  if (!nodeId || !rawKind || index === undefined) return null;
  const kind = KIND_ALIASES[rawKind];
  if (!kind) return null;
  const parsed = Number(index);
  if (!Number.isInteger(parsed) || parsed < 0) return null;
  return { nodeId, kind, index: parsed };
}

/** Every primitive has an origin; derived steps inherit the upstream origin. */
export function originOf(graph: NodeGraph, node: GraphNode, seen = new Set<string>()): Vec2 {
  const ox = node.params['ox'];
  const oy = node.params['oy'];
  if (Number.isFinite(ox) && Number.isFinite(oy)) return Vec2.of(ox as number, oy as number);
  // A cycle can still reach here (hand-edited or injected graphs), so stop instead of recursing.
  if (seen.has(node.id)) return Vec2.of(0, 0);
  seen.add(node.id);
  const upstream = upstreamOf(graph, node.id);
  return upstream ? originOf(graph, upstream, seen) : Vec2.of(0, 0);
}

export function verticesOf(entities: readonly Entity[]): Vec2[] {
  const points: Vec2[] = [];
  for (const entity of entities) {
    switch (entity.type) {
      case 'line':
        points.push(entity.a, entity.b);
        break;
      case 'polyline':
        points.push(...entity.vertices);
        break;
      case 'text':
        points.push(entity.position);
        break;
      default:
        break;
    }
  }
  return points;
}

export function centersOf(entities: readonly Entity[]): Vec2[] {
  const points: Vec2[] = [];
  for (const entity of entities) {
    if (entity.type === 'circle' || entity.type === 'arc' || entity.type === 'ellipse') {
      points.push(entity.center);
    }
  }
  return points;
}

/** The four quadrant directions, named so a pick reads as "top" rather than "3". */
const QUADRANT_DIRECTIONS: ReadonlyArray<{ label: string; angle: number }> = [
  { label: 'Right', angle: 0 },
  { label: 'Top', angle: Math.PI / 2 },
  { label: 'Left', angle: Math.PI },
  { label: 'Bottom', angle: (3 * Math.PI) / 2 },
];

/**
 * The five grips of a round shape: its quadrants, in the same order the picker lists them. An arc
 * only reaches some of them, so both the list and the names filter together — a pick can never
 * point at a quadrant the shape does not have.
 */
export function quadrantsOf(
  entities: readonly Entity[],
): Array<{ point: Vec2; label: string }> {
  const out: Array<{ point: Vec2; label: string }> = [];
  for (const entity of entities) {
    const points = entityQuadrants(entity);
    if (entity.type === 'arc') {
      QUADRANT_DIRECTIONS.filter((direction) =>
        angleInArc(direction.angle, entity.startAngle, entity.endAngle, entity.ccw),
      ).forEach((direction, index) => {
        const point = points[index];
        if (point) out.push({ point, label: direction.label });
      });
      continue;
    }
    points.forEach((point, index) => {
      const direction = QUADRANT_DIRECTIONS[index];
      if (direction) out.push({ point, label: direction.label });
    });
  }
  return out;
}

export function edgesOf(entities: readonly Entity[]): Array<[Vec2, Vec2]> {  const edges: Array<[Vec2, Vec2]> = [];
  for (const entity of entities) {
    if (entity.type === 'line') edges.push([entity.a, entity.b]);
    if (entity.type === 'polyline') {
      for (let i = 0; i + 1 < entity.vertices.length; i++) {
        edges.push([entity.vertices[i]!, entity.vertices[i + 1]!]);
      }
      if (entity.closed && entity.vertices.length > 2) {
        edges.push([entity.vertices[entity.vertices.length - 1]!, entity.vertices[0]!]);
      }
    }
  }
  return edges;
}

/** A point where two entities of the stream cross, with the names on each side. */
export interface IntersectionPoint {
  point: Vec2;
  a: string;
  b: string;
}

/** Every crossing inside one stream, in a stable order (entity order, then pair order, then the
 * geometry's own order). This is what makes an intersection pickable like any other point. */
export function intersectionsOf(entities: readonly Entity[]): IntersectionPoint[] {
  const out: IntersectionPoint[] = [];
  for (let i = 0; i < entities.length; i++) {
    for (let j = i + 1; j < entities.length; j++) {
      const a = entities[i]!;
      const b = entities[j]!;
      for (const point of intersectEntities(a, b, { chordTolerance: 0.01 })) {
        // The same corner reached by two edges is one candidate, not two.
        if (out.some((existing) => existing.point.distanceTo(point) < 1e-6)) continue;
        out.push({ point, a: a.name ?? a.id, b: b.name ?? b.id });
      }
    }
  }
  return out;
}

/**
 * The same crossings, remembered against the stream they came from.
 *
 * Streams are memoised, so the array identity is a valid key: every picker, every card and every
 * hover asks for the crossings of the *same* array, and each one only has to be computed once per
 * change.
 */
const crossingMemos = new WeakMap<readonly Entity[], IntersectionPoint[]>();

export function crossingsOf(entities: readonly Entity[]): IntersectionPoint[] {
  const known = crossingMemos.get(entities);
  if (known) return known;
  const computed = intersectionsOf(entities);
  crossingMemos.set(entities, computed);
  return computed;
}

/** Resolves a reference to a world point, evaluating the referenced step when needed. */
export function refPoint(
  graph: NodeGraph,
  ref: NodeRef,
  resolve: (node: GraphNode) => Entity[],
): Vec2 | null {
  const node = graph.nodes.find((candidate) => candidate.id === ref.nodeId);
  if (!node) return null;
  if (ref.kind === 'origin') return originOf(graph, node);

  const entities = resolve(node);
  switch (ref.kind) {
    case 'vertex':
      return verticesOf(entities)[ref.index] ?? null;
    case 'center':
      return centersOf(entities)[ref.index] ?? null;
    case 'quadrant':
      return quadrantsOf(entities)[ref.index]?.point ?? null;
    case 'path':
      return pathPoint(entities[ref.index]);
    case 'intersection':
      return crossingsOf(entities)[ref.index]?.point ?? null;
    case 'entity': {
      // An object is a shape the step *drew*, not something it passes on: index the step's own
      // output only, so "Circle · C2" means C2's circle and not the plate under it.
      const own = ownEntitiesOf(entities, ref.nodeId);
      const entity = own[ref.index];
      return entity ? pathPoint(entity) ?? centerOf(entity) : null;
    }
    default: {
      const edge = edgesOf(entities)[ref.index];
      return edge ? Vec2.mid(edge[0], edge[1]) : null;
    }
  }
}

/** Index of the last round entity in a stream, or -1. */
function lastCircleIndex(stream: readonly Entity[]): number {
  for (let index = stream.length - 1; index >= 0; index--) {
    const type = stream[index]!.type;
    if (type === 'circle' || type === 'arc') return index;
  }
  return -1;
}

/** The shapes a step drew itself, in stream order — what an object pick can name. */
function ownEntitiesOf(entities: readonly Entity[], nodeId: string): Entity[] {
  const own = entities.filter((entity) => entity.source === nodeId);
  // Graphs written before provenance existed have no `source`: fall back to the whole stream.
  return own.length > 0 ? own : [...entities];
}

/** The centre of a round entity, so an object pick has something to draw at. */
function centerOf(entity: Entity): Vec2 | null {
  if (entity.type === 'circle' || entity.type === 'arc' || entity.type === 'ellipse') {
    return entity.center;
  }
  return null;
}

export interface RefOption {
  value: string;
  label: string;
  /** Where the picker draws this candidate, in world millimetres. */
  point: Vec2;
  /** What kind of point this is, so a picker can mark crossings differently from vertices. */
  kind: RefKind;
  /** For an object pick: its outline, so a click anywhere on it counts. */
  paths?: Array<{ points: Vec2[]; closed: boolean }>;
}

const ALL_REF_KINDS: readonly RefKind[] = [
  'origin',
  'vertex',
  'center',
  'quadrant',
  'midline',
  'path',
  'intersection',
];

/** Entities that can act as an array path (anything with a flattened spine). */
export function isPathEntity(entity: Entity): boolean {
  return (
    entity.type === 'line' ||
    entity.type === 'polyline' ||
    entity.type === 'circle' ||
    entity.type === 'arc' ||
    entity.type === 'ellipse'
  );
}

/** The flattened spine of an entity, as one open point run. */
export function pathPoints(entity: Entity): Vec2[] {
  const path = entityPaths(entity, { chordTolerance: 0.05 });
  if (path.length === 0) return [];
  const points = [...path[0]!.points];
  if (path[0]!.closed && points.length > 0) points.push(points[0]!);
  return points;
}

/**
 * The one point a `path` reference stands for.
 *
 * A circular shape resolves to the point opposite its seam — the left of a circle — because that is
 * where the picker marks it, and everything else takes the middle of its spine by length. What the
 * field promises is therefore exactly what the step snaps to.
 */
export function pathPoint(entity: Entity | undefined): Vec2 | null {
  if (!entity) return null;
  if (entity.type === 'circle') {
    return Vec2.of(entity.center.x - entity.radius, entity.center.y);
  }
  if (entity.type === 'arc') {
    const mid = entity.startAngle + arcSweep(entity.startAngle, entity.endAngle, entity.ccw) / 2;
    return Vec2.polar(entity.radius, mid).add(entity.center);
  }
  if (entity.type === 'ellipse') {
    return Vec2.polar(entity.rx, Math.PI).rotate(entity.rotation).add(entity.center);
  }
  const points = pathPoints(entity);
  if (points.length === 0) return null;
  if (points.length === 1) return points[0]!;
  const closed = points[0]!.equals(points[points.length - 1]!);
  // A closed spine spaces by total/count, so two stations give the seam and its opposite.
  const stations = samplePathAlong(points, closed ? 2 : 3);
  return stations[1]?.point ?? points[Math.floor(points.length / 2)]!;
}

/** True when a point on the arc's own circle actually lies on the arc. */
function arcContains(entity: Entity, point: Vec2): boolean {
  if (entity.type !== 'arc') return true;
  const sweep = arcSweep(entity.startAngle, entity.endAngle, entity.ccw);
  const angle = point.sub(entity.center).angle();
  const delta = entity.ccw
    ? normalizeAngle(angle - entity.startAngle)
    : normalizeAngle(entity.startAngle - angle);
  return delta <= sweep + 1e-9;
}

/**
 * Where a line from `from` touches a circle or arc tangentially.
 *
 * The line is a leg of the right triangle `from → point → centre`, so its length is √(d² − r²)
 * and it leans off the direction to the centre by `acos(length / d)`. A point inside the shape has
 * no tangent at all, which is why the answer can be `null` — the caller then falls back to its
 * angle so the step never silently loses its line.
 */
export function tangentPoint(
  from: Vec2,
  entity: Entity | null | undefined,
  side: 'left' | 'right' = 'left',
): Vec2 | null {
  if (!entity || (entity.type !== 'circle' && entity.type !== 'arc')) return null;
  const radius = entity.radius;
  if (!(radius > 0)) return null;
  const toCenter = entity.center.sub(from);
  const distance = toCenter.len();
  if (distance <= radius + 1e-9) return null;
  const reach = Math.sqrt(distance * distance - radius * radius);
  const lean = Math.acos(Math.min(1, reach / distance));
  const base = toCenter.angle();
  // Two tangents touch the circle; the side picks one and an arc may only carry one of them.
  for (const sign of side === 'right' ? [-1, 1] : [1, -1]) {
    const point = from.add(Vec2.polar(reach, base + sign * lean));
    if (arcContains(entity, point)) return point;
  }
  return null;
}

/** Resolves a `path` reference to the entity it points at. */
export function refPath(
  graph: NodeGraph,
  ref: NodeRef,
  resolve: (node: GraphNode) => Entity[],
): Entity | null {
  if (ref.kind !== 'path') return null;
  const node = graph.nodes.find((candidate) => candidate.id === ref.nodeId);
  if (!node) return null;
  const entity = resolve(node)[ref.index];
  return entity && isPathEntity(entity) ? entity : null;
}

/** A reflection axis: the infinite line through two points. */
export interface Axis {
  a: Vec2;
  b: Vec2;
}

/** The axis a whole entity stands for: a line is its own axis, a circle its horizontal diameter. */
function axisOfEntity(entity: Entity): Axis | null {
  if (entity.type === 'line') return { a: entity.a, b: entity.b };
  if (entity.type === 'circle' || entity.type === 'arc') {
    const center = entity.center;
    return {
      a: Vec2.of(center.x - entity.radius, center.y),
      b: Vec2.of(center.x + entity.radius, center.y),
    };
  }
  if (entity.type === 'ellipse') {
    const center = entity.center;
    return { a: Vec2.of(center.x - entity.rx, center.y), b: Vec2.of(center.x + entity.rx, center.y) };
  }
  const points = pathPoints(entity);
  if (points.length >= 2) return { a: points[0]!, b: points[points.length - 1]! };
  return null;
}

/**
 * Resolves one reference to a mirror axis. Only geometry names an axis: an edge reference is the
 * edge itself and a path reference is the entity itself (a line reflects across itself, a circle
 * across its diameter). Points carry no direction, so they are not axis candidates.
 */
export function refAxis(
  graph: NodeGraph,
  ref: NodeRef,
  resolve: (node: GraphNode) => Entity[],
): Axis | null {
  const node = graph.nodes.find((candidate) => candidate.id === ref.nodeId);
  if (!node) return null;
  const entities = resolve(node);

  if (ref.kind === 'midline') {
    const edge = edgesOf(entities)[ref.index];
    return edge ? { a: edge[0], b: edge[1] } : null;
  }
  if (ref.kind === 'path') {
    const entity = entities[ref.index];
    return entity ? axisOfEntity(entity) : null;
  }
  return null;
}

/**
 * Which way an axis runs. An edge is almost always horizontal or vertical, so this is what tells
 * the card that an axis flips X or flips Y instead of reflecting at a free angle.
 */
export function axisKind(axis: Axis): 'horizontal' | 'vertical' | 'free' {
  const dx = Math.abs(axis.b.x - axis.a.x);
  const dy = Math.abs(axis.b.y - axis.a.y);
  if (dy <= 1e-9 && dx > 1e-9) return 'horizontal';
  if (dx <= 1e-9 && dy > 1e-9) return 'vertical';
  return 'free';
}

export interface PathSample {
  point: Vec2;
  /** Tangent direction of the path at `point`, in radians. */
  angle: number;
}

/**
 * `count` evenly spaced stations along a path, measured by arc length — the placement grid a
 * path array lands its copies on. Closed paths space by `total / count` so the seam is not
 * duplicated.
 */
export function samplePathAlong(points: readonly Vec2[], count: number): PathSample[] {
  const total = count < 1 ? 0 : Math.max(1, Math.round(count));
  if (points.length === 0) return [];
  if (points.length === 1) {
    return Array.from({ length: total }, () => ({ point: points[0]!, angle: 0 }));
  }

  const last = points[points.length - 1]!;
  const closed = points[0]!.equals(last);
  const lengths: number[] = [0];
  for (let index = 1; index < points.length; index++) {
    lengths.push(lengths[index - 1]! + points[index - 1]!.distanceTo(points[index]!));
  }
  const length = lengths[lengths.length - 1]!;
  if (length <= 0) {
    return Array.from({ length: total }, () => ({ point: points[0]!, angle: 0 }));
  }

  const out: PathSample[] = [];
  for (let step = 0; step < total; step++) {
    const fraction = closed ? step / total : total === 1 ? 0 : step / (total - 1);
    const target = length * fraction;
    let segment = 1;
    // `<=` so a station that lands exactly on a vertex takes the *outgoing* tangent, matching
    // the start of the path (otherwise the first copy and the rest disagree by half a segment).
    while (segment < lengths.length - 1 && lengths[segment]! <= target) segment++;
    const from = points[segment - 1]!;
    const to = points[segment]!;
    const span = lengths[segment]! - lengths[segment - 1]!;
    const t = span > 0 ? (target - lengths[segment - 1]!) / span : 0;
    out.push({ point: from.lerp(to, t), angle: to.sub(from).angle() });
  }
  return out;
}

/** True for the three constraint steps: Coincident, Tangent, and the older combined one. */
export function isConstraintNode(node: GraphNode): boolean {
  return node.type === 'coincident' || node.type === 'tangent' || node.type === 'constraint';
}

/** Which demand an older combined constraint names. */
function constraintKindOf(node: GraphNode): 'coincident' | 'tangent' {
  return Math.round(node.params['kind'] ?? 0) >= 1 ? 'tangent' : 'coincident';
}

/** The steps that reach the output sink — everything else is drawn nowhere. */
export function liveNodes(graph: NodeGraph): Set<string> {
  const live = new Set<string>();
  const output = graph.nodes.find((node) => node.type === OUTPUT_NODE_TYPE);
  if (!output) return live;
  const queue = [output.id];
  while (queue.length > 0) {
    const id = queue.pop()!;
    if (live.has(id)) continue;
    live.add(id);
    for (const edge of graph.edges) {
      if (edge.to === id && !live.has(edge.from)) queue.push(edge.from);
    }
  }
  return live;
}

/** Picker contents: origin, vertices, centres, midlines and paths of every other step. */
export function refOptions(
  graph: NodeGraph,
  exceptNodeId: string,
  evaluate: (nodeId: string) => Entity[] = () => [],
  kinds: readonly RefKind[] = ALL_REF_KINDS,
): RefOption[] {
  const out: RefOption[] = [];
  const wanted = new Set(kinds);
  // Only steps that actually reach the output draw anything, so only they offer snapping points.
  const live = liveNodes(graph);
  const push = (node: GraphNode, kind: RefKind, index: number, point: Vec2): void => {
    out.push({
      value: serializeRef({ nodeId: node.id, kind, index }),
      label: `${NODE_DEFS[node.type].title} · ${REF_KIND_LABELS[kind]} ${index + 1}`,
      point,
      kind,
    });
  };

  for (const node of graph.nodes) {
    if (node.id === exceptNodeId || node.type === OUTPUT_NODE_TYPE) continue;
    // A step wired nowhere is not part of the drawing: its points are not snapping points.
    if (!live.has(node.id)) continue;
    // Maths steps produce numbers, so they are not geometry pick targets.
    if (isValueNode(node)) continue;
    if (wanted.has('origin')) {
      out.push({
        value: serializeRef({ nodeId: node.id, kind: 'origin', index: 0 }),
        label: `${NODE_DEFS[node.type].title} · Origin`,
        point: originOf(graph, node),
        kind: 'origin',
      });
    }
    const entities = evaluate(node.id);
    if (wanted.has('vertex')) {
      verticesOf(entities).forEach((point, index) => push(node, 'vertex', index, point));
    }
    if (wanted.has('center')) {
      centersOf(entities).forEach((point, index) => push(node, 'center', index, point));
    }
    if (wanted.has('quadrant')) {
      // Named by direction, so a circle offers centre + top/right/bottom/left and nothing else.
      quadrantsOf(entities).forEach((quadrant, index) => {
        out.push({
          value: serializeRef({ nodeId: node.id, kind: 'quadrant', index }),
          label: `${NODE_DEFS[node.type].title} · ${quadrant.label} quadrant`,
          point: quadrant.point,
          kind: 'quadrant',
        });
      });
    }
    if (wanted.has('midline')) {
      edgesOf(entities).forEach(([a, b], index) => push(node, 'midline', index, a.lerp(b, 0.5)));
    }
    if (wanted.has('path')) {
      entities.forEach((entity, index) => {
        if (!isPathEntity(entity)) return;
        push(node, 'path', index, pathPoint(entity) ?? Vec2.of(0, 0));
      });
    }
    if (wanted.has('entity')) {
      // The object itself, not a point on it: a tangency touches it wherever it must.
      ownEntitiesOf(entities, node.id).forEach((entity, index) => {
        const at = pathPoint(entity) ?? centerOf(entity);
        if (!at) return;
        out.push({
          value: serializeRef({ nodeId: node.id, kind: 'entity', index }),
          label: `${NODE_DEFS[node.type].title} · ${entity.name ?? `Object ${index + 1}`}`,
          point: at,
          kind: 'entity',
          paths: entityPaths(entity),
        });
      });
    }
    // Crossings are points like any other: a field that takes a vertex takes a crossing too.
    if (wanted.has('intersection') || wanted.has('vertex')) {
      crossingsOf(entities).forEach((crossing, index) => {
        out.push({
          value: serializeRef({ nodeId: node.id, kind: 'intersection', index }),
          label: `Intersection ${index + 1} · ${crossing.a} × ${crossing.b}`,
          point: crossing.point,
          kind: 'intersection',
        });
      });
    }
  }
  return out;
}

// ------------------------------------------------------------------ trim edges

/**
 * A trim in progress: every span it produced, as an independent named edge. The node only
 * hides what the user removed, so restoring an edge is a matter of re-ticking it.
 */
export interface TrimEdge {
  /** Stable key: source entity name plus span index, e.g. `R1.2`. */
  key: string;
  label: string;
  source: string;
  entity: Entity;
  kept: boolean;
}

/** Splits a stream at its intersections and names every resulting span. */
export function splitStreamIntoEdges(entities: readonly Entity[]): TrimEdge[] {
  const out: TrimEdge[] = [];
  let counter = 0;
  entities.forEach((entity, streamIndex) => {
    const pieces = splitEntityAt(entity, cutPointsFor(entity, entities));
    pieces.forEach((piece, index) => {
      counter += 1;
      out.push({
        // Position-based, not name-based: two different spans can carry the same name (a step's
        // second result is called `C2.2`, which is also what the second piece of `C2` is called),
        // and a shared key would trim two spans with one entry.
        key: `e${streamIndex}.${index + 1}`,
        label: `Edge ${counter}`,
        source: entity.name ?? entity.id,
        entity: piece,
        kept: true,
      });
    });
  });
  return out;
}

/** A movable point of the stream: which entity it belongs to and the handle that names it. */
export interface HandleSpot {
  /** Position of the entity in the stream the pick was resolved against. */
  index: number;
  handle: string;
}

/**
 * Where a point pick actually lives. A vertex pick indexes `verticesOf` and a centre pick indexes
 * `centersOf`, so this walks the stream the same way those helpers do and keeps the handle names
 * the geometry layer uses (`a`, `b`, `v0…`, `c`, `p`).
 *
 * The answer is a stream *position*, not a name: every step renames the stream it passes on, so a
 * name would stop matching one step later.
 */
export function handleSpotAt(
  entities: readonly Entity[],
  kind: RefKind,
  index: number,
): HandleSpot | null {
  const spots: HandleSpot[] = [];
  if (kind === 'vertex') {
    entities.forEach((entity, position) => {
      if (entity.type === 'line') {
        spots.push({ index: position, handle: 'a' }, { index: position, handle: 'b' });
      }
      if (entity.type === 'polyline') {
        entity.vertices.forEach((_, vertex) => spots.push({ index: position, handle: `v${vertex}` }));
      }
      if (entity.type === 'text') spots.push({ index: position, handle: 'p' });
    });
    return spots[index] ?? null;
  }
  if (kind === 'center') {
    entities.forEach((entity, position) => {
      if (entity.type === 'circle' || entity.type === 'arc' || entity.type === 'ellipse') {
        spots.push({ index: position, handle: 'c' });
      }
    });
    return spots[index] ?? null;
  }
  return null;
}

/** The trim node's edge list with the user's keep/remove state applied. */
export function trimEdges(graph: NodeGraph, nodeId: string, layerId = '0'): TrimEdge[] {
  const node = graph.nodes.find((candidate) => candidate.id === nodeId);
  if (!node) return [];
  // The same stream the trim step itself sees — every wire, not just the first one — or the keys
  // the card and the picker use would not be the keys the step is hiding.
  const input = stepInputEntities(graph, nodeId, layerId);
  const hidden = new Set(node.hidden ?? []);
  return splitStreamIntoEdges(input).map((edge) => ({ ...edge, kept: !hidden.has(edge.key) }));
}

// -------------------------------------------------------------- picked pieces

/** Key of one piece of a step's incoming stream, as stored on a node's `picks`. */
export function pickKey(index: number): string {
  return `e${index}`;
}

/**
 * A whole entity of a step's incoming stream that a pick operation can select — what the mirror
 * step reflects when it is set to "picked pieces" instead of "previous shape".
 */
export interface PickedPiece {
  key: string;
  label: string;
  entity: Entity;
  picked: boolean;
}

/** The entities one step receives, in the order it consumes them: A then B for a merge. */
export function stepInputEntities(graph: NodeGraph, nodeId: string, layerId = '0'): Entity[] {
  const node = graph.nodes.find((candidate) => candidate.id === nodeId);
  if (!node) return [];
  const slots = nodeInputs(node);
  const sources =
    slots.length > 0
      ? slots
          .map((input) => upstreamOf(graph, nodeId, input.slot))
          .filter((candidate): candidate is GraphNode => Boolean(candidate))
      : upstreamsOf(graph, nodeId);
  return sources.flatMap((source) => evaluateNode(graph, source.id, layerId));
}

/** The incoming stream of one step, with what the user picked in the drawing. */
export function pickablePieces(graph: NodeGraph, nodeId: string, layerId = '0'): PickedPiece[] {
  const node = graph.nodes.find((candidate) => candidate.id === nodeId);
  if (!node) return [];
  const input = stepInputEntities(graph, nodeId, layerId);
  const picks = new Set(node.picks ?? []);
  return input.map((entity, index) => ({
    key: pickKey(index),
    label: entity.name ?? entity.id,
    entity,
    picked: picks.has(pickKey(index)),
  }));
}

/**
 * The axis a mirror step reflects across: the picked axis, else the two points older graphs
 * stored, else a line through the origin at the step's `axis angle`.
 */
function mirrorAxisOf(
  graph: NodeGraph,
  node: GraphNode,
  resolve: (node: GraphNode) => Entity[],
  angleDeg: number,
): Axis {
  const axisRef = parseRef(node.refs?.['axis']);
  const picked = axisRef ? refAxis(graph, axisRef, resolve) : null;
  if (picked) return picked;

  const from = parseRef(node.refs?.['p1']);
  const to = parseRef(node.refs?.['p2']);
  const a = from ? refPoint(graph, from, resolve) : null;
  const b = to ? refPoint(graph, to, resolve) : null;
  if (a && b && a.distanceTo(b) > 1e-9) return { a, b };

  const origin = a ?? Vec2.of(0, 0);
  return { a: origin, b: origin.add(Vec2.polar(1000, deg2rad(angleDeg))) };
}

/** The axis a mirror step reflects across right now — the picker draws it while picking. */
export function mirrorAxis(graph: NodeGraph, nodeId: string, layerId = '0'): Axis | null {
  const node = graph.nodes.find((candidate) => candidate.id === nodeId);
  if (!node || node.type !== 'mirror') return null;
  const { resolve } = makeResolver(graph, layerId);
  return mirrorAxisOf(graph, node, resolve, paramValue(graph, node, 'angle', 90));
}

// ----------------------------------------------------------------- evaluation

function valueOf(node: GraphNode, id: string, fallback: number): number {
  const raw = node.params[id];
  return Number.isFinite(raw) ? (raw as number) : fallback;
}

/** Choice index of a boolean step → the operator it names. */
function booleanModeOf(choice: number): BooleanMode {
  const rounded = Math.round(choice);
  return rounded === 0 ? 'intersect' : rounded === 2 ? 'inverse' : 'union';
}

/** True for steps that produce a number instead of geometry. */
export function isValueNode(node: GraphNode): boolean {
  return Boolean(NODE_DEFS[node.type]?.value);
}

const round = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * The number a value step computes, or `null` when the node is missing, is not a value step, or
 * sits in a cycle. Bindings chain arbitrarily deep, so `Multiply` can take `Add` as an operand,
 * and a `Measure` reads the shape some other step produced.
 */
export function evaluateValue(
  graph: NodeGraph,
  nodeId: string,
  resolve?: (node: GraphNode) => Entity[],
): number | null {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const visiting = new Set<string>();
  let streams: ((node: GraphNode) => Entity[]) | null = null;
  const streamOf = (target: GraphNode): Entity[] => {
    streams ??= resolve ?? ((node: GraphNode) => evaluateNode(graph, node.id));
    return streams(target);
  };

  const compute = (id: string): number | null => {
    if (visiting.has(id)) return null;
    const node = byId.get(id);
    if (!node || !NODE_DEFS[node.type]?.value) return null;
    visiting.add(id);
    const operand = (paramId: string, fallback: number): number => {
      const bound = node.values?.[paramId];
      if (bound) {
        const resolved = resolveBoundValue(graph, bound, streams ?? undefined);
        if (resolved !== null) return resolved;
      }
      return valueOf(node, paramId, fallback);
    };

    let result: number;
    switch (node.type) {
      case 'number':
        result = operand('value', 0);
        break;
      case 'measure': {
        const ref = parseRef(node.refs?.['of']);
        const mode = measureModeOf(node);
        const target = ref
          ? byId.get(ref.nodeId)
          : // No pick: measure everything that reaches the output.
            findOutputNode(graph);
        const stream = target ? streamOf(target) : [];
        if (mode === 'count') {
          result = stream.length;
          break;
        }
        if (ref) {
          const entity = stream[ref.index];
          result = entity ? measureEntity(entity, mode) : 0;
          break;
        }
        if (mode === 'length') {
          result = stream.reduce((sum, entity) => sum + measureEntity(entity, 'length'), 0);
          break;
        }
        if (mode === 'radius') {
          result = stream.reduce((max, entity) => Math.max(max, measureEntity(entity, 'radius')), 0);
          break;
        }
        const box = entitiesBox(stream);
        result = !box
          ? 0
          : mode === 'width'
            ? box.width
            : mode === 'height'
              ? box.height
              : mode === 'x'
                ? box.center.x
                : box.center.y;
        break;
      }
      case 'add':
        result = operand('a', 0) + operand('b', 0);
        break;
      case 'subtract':
        result = operand('a', 0) - operand('b', 0);
        break;
      case 'multiply':
        result = operand('a', 1) * operand('b', 1);
        break;
      case 'divide': {
        const divisor = operand('b', 1);
        result = divisor === 0 ? 0 : operand('a', 0) / divisor;
        break;
      }
      default:
        result = 0;
    }

    visiting.delete(id);
    return Number.isFinite(result) ? result : null;
  };

  return compute(nodeId);
}

/** A param's effective number: the bound step when there is one, else the typed value. */
export function paramValue(
  graph: NodeGraph,
  node: GraphNode,
  paramId: string,
  fallback: number,
  resolve?: (node: GraphNode) => Entity[],
): number {
  const bound = node.values?.[paramId];
  if (bound) {
    const resolved = resolveBoundValue(graph, bound, resolve);
    if (resolved !== null) return resolved;
  }
  return valueOf(node, paramId, fallback);
}

// ------------------------------------------------------------ value sources

/**
 * A value can come from a maths step, or straight from a **shape's measurement** — that is the
 * point of "A + B": two shapes can drive a third one without a Measure step in between.
 *
 * Shape sources are encoded in the same `values` map as `measure:<nodeId>:<mode>`, so a binding is
 * still one string on the node and needs no extra state.
 */
export interface MeasureSource {
  nodeId: string;
  mode: MeasureMode;
}

export function measureSourceId(nodeId: string, mode: MeasureMode): string {
  return `measure:${nodeId}:${mode}`;
}

export function parseMeasureSource(id: string | undefined): MeasureSource | null {
  if (!id || !id.startsWith('measure:')) return null;
  const [, nodeId, mode] = id.split(':');
  if (!nodeId || !mode) return null;
  return (MEASURE_ORDER as readonly string[]).includes(mode)
    ? { nodeId, mode: mode as MeasureMode }
    : null;
}

/** The measurements a step offers, chosen from what it actually produced. */
const MEASURES_BY_KIND: Record<string, readonly MeasureMode[]> = {
  circle: ['radius', 'x', 'y'],
  arc: ['radius', 'length'],
  ellipse: ['width', 'height'],
  line: ['length', 'x', 'y'],
  polyline: ['width', 'height'],
  spline: ['width', 'height'],
  dimension: ['width', 'x', 'y'],
};

/** Reads one measurement off a step's last entity (or its whole stream for a count). */
export function measureStepValue(
  graph: NodeGraph,
  nodeId: string,
  mode: MeasureMode,
  resolve?: (node: GraphNode) => Entity[],
): number | null {
  const node = graph.nodes.find((candidate) => candidate.id === nodeId);
  if (!node) return null;
  const stream = resolve ? resolve(node) : evaluateNode(graph, nodeId, '0');
  if (mode === 'count') return stream.length;
  const entity = stream[stream.length - 1];
  if (!entity) return null;
  return measureEntity(entity, mode);
}

/** Resolves one binding: a maths step id, or a `measure:` shape source. */
let valueDepth = 0;
function resolveBoundValue(
  graph: NodeGraph,
  bound: string,
  resolve?: (node: GraphNode) => Entity[],
): number | null {
  // Hand-edited files can bind in a circle; stop rather than recursing forever.
  if (valueDepth > 8) return null;
  valueDepth += 1;
  try {
    const measure = parseMeasureSource(bound);
    if (measure) return measureStepValue(graph, measure.nodeId, measure.mode, resolve);
    return evaluateValue(graph, bound);
  } finally {
    valueDepth -= 1;
  }
}

/** What a binding is called on a card, for a maths step or a shape measurement. */
export function valueSourceLabel(graph: NodeGraph, id: string): string {
  const measure = parseMeasureSource(id);
  if (!measure) return valueLabel(graph, id);
  const node = graph.nodes.find((candidate) => candidate.id === measure.nodeId);
  const value = measureStepValue(graph, measure.nodeId, measure.mode) ?? 0;
  const title = node ? shapeStepName(graph, node) : 'Shape';
  const mode = measure.mode[0]!.toUpperCase() + measure.mode.slice(1);
  return `${title} · ${mode} = ${round(value)}`;
}

/** A shape's card name: its type title and its place among the steps of that type. */
export function shapeStepName(graph: NodeGraph, node: GraphNode): string {
  const same = graph.nodes.filter((candidate) => candidate.type === node.type);
  return `${NODE_DEFS[node.type].title} ${same.findIndex((candidate) => candidate.id === node.id) + 1}`;
}

export interface ValueSourceOption {
  id: string;
  label: string;
}

/** A value step's human name: its position among the value steps, then its value. */
export function valueLabel(graph: NodeGraph, nodeId: string): string {
  const maths = graph.nodes.filter((node) => isValueNode(node));
  const index = maths.findIndex((node) => node.id === nodeId);
  const node = maths[index];
  if (!node) return 'missing step';
  return `${NODE_DEFS[node.type].title} ${index + 1} = ${round(evaluateValue(graph, nodeId) ?? 0)}`;
}

/**
 * Everything that may drive a param of `forNodeId`: the value steps, plus each geometry step's own
 * measurements (`Rectangle 1 · Width`). A shape cannot drive a param of a step it depends on, so
 * loops are refused here rather than crashing the evaluation.
 */
export function valueSources(graph: NodeGraph, forNodeId: string): ValueSourceOption[] {
  const dependsOn = (from: string, target: string, seen = new Set<string>()): boolean => {
    if (from === target) return true;
    if (seen.has(from)) return false;
    seen.add(from);
    const node = graph.nodes.find((candidate) => candidate.id === from);
    return Object.values(node?.values ?? {}).some((bound) => dependsOn(bound, target, seen));
  };

  const out: ValueSourceOption[] = graph.nodes
    .filter((node) => isValueNode(node) && node.id !== forNodeId && !dependsOn(node.id, forNodeId))
    .map((node) => ({ id: node.id, label: valueLabel(graph, node.id) }));

  // Shapes: one entry per useful measurement, so A and B can take "Rectangle 1 · Width" directly.
  const geometry = graph.nodes.filter(
    (node) => !isValueNode(node) && node.type !== OUTPUT_NODE_TYPE && node.id !== forNodeId,
  );
  for (const node of geometry) {
    if (dependsOn(node.id, forNodeId)) continue;
    const stream = evaluateNode(graph, node.id, '0');
    const entity = stream[stream.length - 1];
    if (!entity) continue;
    const kinds = MEASURES_BY_KIND[entity.type];
    if (!kinds) continue;
    const title = shapeStepName(graph, node);
    for (const mode of kinds) {
      const value = measureEntity(entity, mode);
      out.push({
        id: measureSourceId(node.id, mode),
        label: `${title} · ${mode[0]!.toUpperCase()}${mode.slice(1)} = ${round(value)}`,
      });
    }
  }
  return out;
}

/**
 * The numeric fields of a maths step that another step can drive, in card order. They are the
 * step's *inputs*: `A` and `B` of an operator, nothing on a constant.
 */
export function valueInputs(node: GraphNode): NodeParamDef[] {
  if (!isValueNode(node) || node.type === 'number') return [];
  return activeParams(node).filter((param) => !param.choices);
}

/** True when `fromId` may drive a param of `toNodeId` (a value step or a shape, without a loop). */
export function canBindValue(graph: NodeGraph, fromId: string, toNodeId: string): boolean {
  if (fromId === toNodeId) return false;
  return valueSources(graph, toNodeId).some((source) => source.id === fromId);
}

/** Binds (or clears) the maths step driving one param. Returns false when the bind is illegal. */
export function bindValue(graph: NodeGraph, nodeId: string, paramId: string, fromId: string | null): boolean {
  const node = graph.nodes.find((candidate) => candidate.id === nodeId);
  if (!node) return false;
  if (!fromId) {
    if (node.values) delete node.values[paramId];
    return true;
  }
  if (!canBindValue(graph, fromId, nodeId)) return false;
  node.values = { ...(node.values ?? {}), [paramId]: fromId };
  return true;
}

function entitiesBox(entities: readonly Entity[]): BBox | null {
  let box = BBox.empty();
  for (const entity of entities) box = box.union(entityBBox(entity));
  return box.isEmpty ? null : box;
}

const BEZIER_STEPS = 32;

/** Point on a cubic Bézier at `t`. */
export function bezierAt(node: GraphNode, t: number, graph?: NodeGraph): Vec2 {
  const v = (id: string, fallback: number): number =>
    graph ? paramValue(graph, node, id, fallback) : valueOf(node, id, fallback);
  const p1 = Vec2.of(v('x1', 0), v('y1', 0));
  const p2 = Vec2.of(v('x2', 20), v('y2', 40));
  const p3 = Vec2.of(v('x3', 60), v('y3', 40));
  const p4 = Vec2.of(v('x4', 80), v('y4', 0));
  const u = 1 - t;
  return Vec2.of(
    u * u * u * p1.x + 3 * u * u * t * p2.x + 3 * u * t * t * p3.x + t * t * t * p4.x,
    u * u * u * p1.y + 3 * u * u * t * p2.y + 3 * u * t * t * p3.y + t * t * t * p4.y,
  );
}

function firstPointOf(entity: Entity): Vec2 {
  switch (entity.type) {
    case 'line':
      return entity.a;
    case 'polyline':
      return entity.vertices[0] ?? Vec2.of(0, 0);
    case 'arc':
      return entity.center.add(Vec2.polar(entity.radius, entity.startAngle));
    case 'circle':
      return Vec2.of(entity.center.x + entity.radius, entity.center.y);
    case 'text':
      return entity.position;
    default:
      return Vec2.of(0, 0);
  }
}

/**
 * The far end of a line step, placed the way the step's mode asks for: a picked point (so the line
 * follows what it is pinned to), typed coordinates, a tangent to a circle or arc, or a length and
 * angle.
 */
function lineEndOf(
  node: GraphNode,
  start: Vec2,
  picked: (id: string) => Vec2 | null,
  v: (id: string, fallback: number) => number,
  tangentRef: Entity | null,
): Vec2 {
  switch (lineModeOf(node)) {
    case 'point': {
      const target = picked('target');
      if (target && target.distanceTo(start) > 1e-9) return target;
      break;
    }
    case 'coordinate':
      return Vec2.of(v('endX', start.x + 50), v('endY', start.y));
    case 'tangent': {
      const touch = tangentPoint(start, tangentRef, v('side', 0) >= 0.5 ? 'right' : 'left');
      if (touch) return touch;
      break;
    }
    default:
      break;
  }
  // Length + angle — also the fallback when the picked way has nothing usable to aim at.
  return start.add(Vec2.polar(v('length', 50), deg2rad(v('angle', 0))));
}

function applyNode(
  node: GraphNode,
  input: Entity[],
  graph: NodeGraph,
  layerId: string,
  resolve: (node: GraphNode) => Entity[],
): Entity[] {  const refs = node.refs ?? {};
  // Shape measurements inside a binding read the memoised stream, so `A + B` on shapes is cheap.
  const v = (id: string, fallback: number): number => paramValue(graph, node, id, fallback, resolve);
  const picked = (id: string): Vec2 | null => {
    const ref = parseRef(refs[id]);
    return ref ? refPoint(graph, ref, resolve) : null;
  };
  const anchor = (): Vec2 =>
    picked('reference') ?? Vec2.of(v('ox', 0), v('oy', 0));
  const style = nodeStyle(v);
  // A primitive can be an aid (construction / reference); the boolean operators ignore those.
  const shapeStyle = roleStyle(style, roleOf(v));

  switch (node.type) {
    case 'rect': {
      const start = anchor();
      const width = Math.abs(v('width', 60));
      const height = Math.abs(v('height', 40));
      // Primitives add to the incoming stream, so a chain of steps builds a whole drawing.
      return [
        ...input,
        {
          ...makeRectangle(start, Vec2.of(start.x + width, start.y + height), layerId),
          ...shapeStyle,
        },
      ];
    }
    case 'circle':
      return [...input, { ...makeCircle(anchor(), Math.abs(v('radius', 12)), layerId), ...shapeStyle }];
    case 'line': {
      const start = anchor();
      const tangentRef = parseRef(refs['tangent']);
      const tangent = tangentRef ? refPath(graph, tangentRef, resolve) : null;
      return [
        ...input,
        { ...makeLine(start, lineEndOf(node, start, picked, v, tangent), layerId), ...shapeStyle },
      ];
    }
    case 'bezier': {
      const points: Vec2[] = [];
      for (let step = 0; step <= BEZIER_STEPS; step++) {
        points.push(bezierAt(node, step / BEZIER_STEPS, graph));
      }
      return [...input, { ...makePolyline(points, false, layerId), ...shapeStyle }];
    }
    case 'text': {
      const content = (node.text ?? NODE_DEFS.text.text?.value ?? 'TEXT').trim();
      if (content.length === 0) return [...input];
      return [
        ...input,
        {
          ...makeText(anchor(), content, layerId, {
            height: Math.max(0.1, v('height', 3.5)),
            rotation: deg2rad(v('angle', 0)),
          }),
          ...style,
        },
      ];
    }
    case 'dimension': {
      const from = picked('from') ?? Vec2.of(v('x1', 0), v('y1', 0));
      const to = picked('to') ?? Vec2.of(v('x2', 60), v('y2', 0));
      if (from.distanceTo(to) < 1e-9) return [...input];
      const entity = makeDimension(from, to, v('offset', 10), layerId, {
        kind: v('kind', 0) >= 0.5 ? 'aligned' : 'linear',
        textHeight: Math.max(0.1, v('height', 3.5)),
        precision: Math.max(0, Math.min(6, Math.round(v('decimals', 3)))),
      });
      return [...input, { ...entity, ...style }];
    }
    case 'translate': {
      const target = picked('to');
      const box = entitiesBox(input);
      const dx = target && box ? target.x - box.center.x : v('dx', 0);
      const dy = target && box ? target.y - box.center.y : v('dy', 0);
      return input.map((entity) => withNewId(translateEntity(entity, Vec2.of(dx, dy))));
    }
    case 'rotate': {
      const center = picked('center') ?? Vec2.of(0, 0);
      return input.map((entity) =>
        withNewId(rotateEntity(entity, center, deg2rad(v('angle', 0)))),
      );
    }
    case 'scale': {
      const center = picked('center') ?? Vec2.of(0, 0);
      const factor = v('factor', 2);
      return input.map((entity) => withNewId(scaleEntity(entity, center, factor)));
    }
    case 'mirror': {
      const axis = mirrorAxisOf(graph, node, resolve, v('angle', 90));
      const picked = new Set(node.picks ?? []);
      // "Previous shape" reflects the whole stream; "picked pieces" only what was clicked in the
      // drawing, so one node can mirror a chosen part of a busier model.
      const subjects =
        v('source', 0) >= 0.5
          ? input.filter((_, index) => picked.has(pickKey(index)))
          : input;
      if (subjects.length === 0) return input;
      // Additive: the reflection joins the stream, so the shape that produced it is kept.
      return [
        ...input,
        ...subjects.map((entity) => withNewId(mirrorEntityAcross(entity, axis.a, axis.b))),
      ];
    }
    case 'array': {
      const count = Math.max(1, Math.min(500, Math.round(v('count', 3))));
      if (input.length === 0) return [];
      const mode = arrayModeOf(node);

      if (mode === 'circular') {
        const center = picked('center') ?? Vec2.of(0, 0);
        const step = deg2rad(v('angle', 30));
        const out: Entity[] = [];
        for (let index = 0; index < count; index++) {
          for (const entity of input) {
            out.push(withNewId(rotateEntity(entity, center, step * index)));
          }
        }
        return out;
      }

      if (mode === 'path') {
        const pathRef = parseRef(refs['path']);
        const pathEntity = pathRef ? refPath(graph, pathRef, resolve) : null;
        if (!pathEntity) return input.map((entity) => withNewId(entity));
        // The transform origin is what lands on the path; without it the copies drift.
        const origin = picked('reference') ?? Vec2.of(v('ox', 0), v('oy', 0));
        const align = v('align', 1) !== 0;
        const samples = samplePathAlong(pathPoints(pathEntity), count);
        // Align means "turn with the path": the first copy keeps the authored orientation and
        // later copies follow the change in tangent, so a straight path never rotates anything.
        const baseAngle = samples[0]?.angle ?? 0;
        const out: Entity[] = [];
        for (const sample of samples) {
          const shift = sample.point.sub(origin);
          for (const entity of input) {
            const placed = align ? rotateEntity(entity, origin, sample.angle - baseAngle) : entity;
            out.push(withNewId(translateEntity(placed, shift)));
          }
        }
        return out;
      }

      const dx = v('dx', 80);
      const dy = v('dy', 0);
      const out: Entity[] = [];
      for (let index = 0; index < count; index++) {
        for (const entity of input) {
          out.push(withNewId(translateEntity(entity, Vec2.of(dx * index, dy * index))));
        }
      }
      return out;
    }
    case 'offset': {
      const distance = v('distance', 5);
      const out: Entity[] = [];
      for (const entity of input) {
        const result = offsetEntity(entity, distance, firstPointOf(entity));
        if (result) out.push(withNewId(result));
      }
      return out;
    }
    case 'trim': {
      const hidden = new Set(node.hidden ?? []);
      return splitStreamIntoEdges(input)
        .filter((edge) => !hidden.has(edge.key))
        .map((edge) => withNewId(edge.entity));
    }
    case 'extend':
      return input.map((entity) => withNewId(entity));
    case 'coincident':
    case 'tangent':
    case 'constraint':
      // Constraints are settled for the whole graph before it is drawn (see `solveAdjustments`),
      // so the node itself only passes the stream along.
      return input;
    case 'slice': {
      const againstRef = parseRef(refs['against']);
      const cutterNode = againstRef
        ? graph.nodes.find((candidate) => candidate.id === againstRef.nodeId)
        : undefined;
      const cutters = cutterNode ? resolve(cutterNode) : [];
      if (cutters.length === 0) {
        return sliceAtIntersections(input).map((entity) => withNewId(entity));
      }
      const out: Entity[] = [];
      for (const entity of input) {
        const cuts = cutPointsFor(entity, [...input, ...cutters]);
        out.push(...splitEntityAt(entity, cuts).map((piece) => withNewId(piece)));
      }
      return out;
    }
    case 'merge': {
      // Join only: a merge welds whatever touches into one outline. Cutting shapes against each
      // other belongs to the Boolean step, where the mode is explicit.
      const excluded = new Set(node.picks ?? []);
      const participants: Entity[] = [];
      const untouched: Entity[] = [];
      input.forEach((entity, index) => {
        // Aids stay out of the weld, and so does anything the user excluded: both pass through.
        if (excluded.has(pickKey(index)) || entity.role) untouched.push(withNewId(entity));
        else participants.push(entity);
      });
      const runs = mergeEntities(participants, {
        tolerance: Math.abs(v('gap', 0.05)),
        chordTolerance: Math.abs(v('detail', 0.01)),
      });
      return [
        ...runs.map((run) => ({
          ...makePolyline(run.points, run.closed, layerId),
          ...style,
        })),
        ...untouched,
      ];
    }
    case 'boolean': {
      const mode = booleanModeOf(valueOf(node, 'mode', 1));
      const excluded = new Set(node.picks ?? []);
      const sides: [Entity[], Entity[]] = [[], []];
      const untouched: Entity[] = [];
      let index = 0;
      (['a', 'b'] as const).forEach((slot, side) => {
        const source = upstreamOf(graph, node.id, slot);
        for (const entity of source ? resolve(source) : []) {
          if (excluded.has(pickKey(index)) || entity.role) untouched.push(withNewId(entity));
          else sides[side]!.push(entity);
          index += 1;
        }
      });
      const pieces = booleanEntities(sides[0], sides[1], {
        mode,
        tolerance: Math.abs(v('gap', 0.05)),
        chordTolerance: Math.abs(v('detail', 0.01)),
      });
      // Pieces come back as the geometry they were cut from — an arc stays an arc, a line stays a
      // line — so nothing is tessellated on the way to the drawing, the DXF or the SVG.
      return [...pieces.map((piece) => ({ ...withNewId(piece), layerId, ...style })), ...untouched];
    }
    case 'emit':
      return input;
    case 'number':
    case 'measure':
    case 'add':
    case 'subtract':
    case 'multiply':
    case 'divide':
      // Value steps live beside the geometry stream and never touch it.
      return input;
    default:
      return [];
  }
}

/** Diagnostics: how many steps were actually evaluated and how many came from the cache. */
export const nodeEvalStats = {
  computed: 0,
  reused: 0,
  reset(): void {
    this.computed = 0;
    this.reused = 0;
  },
};

/**
 * Per-graph memo of step streams.
 *
 * A pure `applyNode` call is only worth repeating when the step or something it depends on
 * changed, so every stream is stored with the signature that produced it: the step's own fields,
 * its upstream step's signature, and the constraint offsets applied to it. A picker that re-reads
 * twenty streams, or a validation pass that walks the chain again, then costs map lookups instead
 * of a full re-evaluation.
 */
interface StreamMemo {
  layerId: string;
  streams: Map<string, { signature: string; stream: Entity[] }>;
}

const streamMemos = new WeakMap<NodeGraph, StreamMemo>();

function streamMemo(graph: NodeGraph, layerId: string): StreamMemo {
  let memo = streamMemos.get(graph);
  if (!memo || memo.layerId !== layerId) {
    memo = { layerId, streams: new Map() };
    streamMemos.set(graph, memo);
  }
  // A deleted step must not keep its stream alive.
  if (memo.streams.size > graph.nodes.length) {
    for (const id of [...memo.streams.keys()]) {
      if (!graph.nodes.some((node) => node.id === id)) memo.streams.delete(id);
    }
  }
  return memo;
}

/** Everything about a step that can change its output, as one comparable string. */
function stepSignature(
  node: GraphNode,
  upstreamSignature: string,
  adjustment: Map<number, Adjustment> | undefined,
): string {
  const offsets = adjustment
    ? [...adjustment]
        .map(([position, change]) =>
          change.kind === 'handle'
            ? `${position}h${change.handle}${change.point.x},${change.point.y}`
            : `${position}t${change.delta.x},${change.delta.y}`,
        )
        .join(';')
    : '';
  return [
    node.type,
    JSON.stringify(node.params ?? {}),
    JSON.stringify(node.refs ?? {}),
    JSON.stringify(node.values ?? {}),
    JSON.stringify(node.picks ?? []),
    JSON.stringify(node.hidden ?? []),
    node.text ?? '',
    upstreamSignature,
    offsets,
  ].join('|');
}

/**
 * Records the step behind the geometry it created. What a step passes on untouched keeps the origin
 * it already had, so a mirrored or trimmed plate still points at the rectangle that drew it.
 */
function stampOrigin(entities: Entity[], nodeId: string): Entity[] {
  let changed = false;
  const out = entities.map((entity) => {
    if (entity.source) return entity;
    changed = true;
    return { ...entity, source: nodeId };
  });
  return changed ? out : entities;
}

/** The geometry a step is responsible for — what to light up in the drawing when its card is on. */
export function nodeOwnEntities(graph: NodeGraph, nodeId: string, layerId = '0'): Entity[] {
  const node = graph.nodes.find((candidate) => candidate.id === nodeId);
  if (!node) return [];
  // A trim step *removes* spans, so what it is responsible for is what it cut away: hovering it
  // shows the trimmed lines, not the whole stream it passes on.
  if (node.type === 'trim') {
    return trimEdges(graph, nodeId, layerId)
      .filter((edge) => !edge.kept)
      .map((edge) => edge.entity);
  }
  const output = evaluateNode(graph, nodeId, layerId);
  const own = output.filter((entity) => entity.source === nodeId);
  // A step that only transforms what arrives (move, mirror…) owns nothing: show what it passes on.
  return own.length > 0 ? own : output;
}

/** Evaluates the chain feeding a node, with cycle protection and per-graph memoisation. */
function makeResolver(graph: NodeGraph, layerId: string, adjustments?: Adjustments) {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const cache = new Map<string, Entity[]>();
  const signatures = new Map<string, string>();
  const memo = streamMemo(graph, layerId);
  const visiting = new Set<string>();

  const resolve = (node: GraphNode): Entity[] => {
    const cached = cache.get(node.id);
    if (cached) return cached;
    if (visiting.has(node.id)) return [];
    visiting.add(node.id);

    // A step that declares sockets (merge) evaluates each one: its result C is A + B.
    const slots = nodeInputs(node);
    const upstreams =
      slots.length > 0
        ? slots
            .map((input) => upstreamOf(graph, node.id, input.slot))
            .filter((node): node is GraphNode => Boolean(node))
        : upstreamsOf(graph, node.id);
    const input = upstreams.flatMap((upstream) => resolve(byId.get(upstream.id)!));
    const signature = stepSignature(
      node,
      // The upstream *ids* are part of the key: two identical steps are not interchangeable.
      upstreams
        .map((upstream) => `${upstream.id}#${signatures.get(upstream.id) ?? ''}`)
        .join('|'),
      adjustments?.get(node.id),
    );
    // Nothing this step depends on changed: hand back the stream that was already built.
    const remembered = memo.streams.get(node.id);
    if (remembered && remembered.signature === signature) {
      nodeEvalStats.reused += 1;
      signatures.set(node.id, signature);
      visiting.delete(node.id);
      cache.set(node.id, remembered.stream);
      return remembered.stream;
    }

    nodeEvalStats.computed += 1;
    const produced = stampOrigin(applyNode(node, input, graph, layerId, resolve), node.id);
    // Constraints rewrite the step that owns the geometry, so everything after it — including
    // steps that reference it by pick — sees the constrained shape.
    const result = applyAdjustments(produced, adjustments?.get(node.id));
    // The sink keeps the names of the step that produced the geometry.
    const named =
      node.type === OUTPUT_NODE_TYPE
        ? result
        : namePieces(`${NAME_PREFIX[node.type] ?? 'P'}${node.id.slice(1)}`, result);

    visiting.delete(node.id);
    cache.set(node.id, named);
    signatures.set(node.id, signature);
    memo.streams.set(node.id, { signature, stream: named });
    return named;
  };

  return { byId, resolve };
}

/**
 * One change a constraint forces on a step's stream. A handle move keeps the rest of the shape
 * where it is (a line's end slides along); a translation carries the whole entity, which is what a
 * quadrant, a midpoint, a path point or a crossing needs.
 */
type Adjustment =
  | { kind: 'handle'; handle: string; point: Vec2 }
  | { kind: 'translate'; delta: Vec2 };

/** node id → stream position → change. */
type Adjustments = Map<string, Map<number, Adjustment>>;

function applyAdjustments(
  entities: Entity[],
  changes: Map<number, Adjustment> | undefined,
): Entity[] {
  if (!changes || changes.size === 0) return entities;
  return entities.map((entity, index) => {
    const change = changes.get(index);
    if (!change) return entity;
    return change.kind === 'handle'
      ? withHandlePoint(entity, change.handle, change.point)
      : translateEntity(entity, change.delta);
  });
}

function sameAdjustments(a: Adjustments, b: Adjustments): boolean {
  if (a.size !== b.size) return false;
  for (const [nodeId, changes] of a) {
    const other = b.get(nodeId);
    if (!other || other.size !== changes.size) return false;
    for (const [position, change] of changes) {
      const rival = other.get(position);
      if (!rival || rival.kind !== change.kind) return false;
      if (change.kind === 'handle' && rival.kind === 'handle') {
        if (change.handle !== rival.handle) return false;
        if (change.point.distanceTo(rival.point) > 1e-9) return false;
      }
      if (change.kind === 'translate' && rival.kind === 'translate') {
        if (change.delta.distanceTo(rival.delta) > 1e-9) return false;
      }
    }
  }
  return true;
}

/** Position in the stream of the entity a point pick belongs to, or -1 when it names no shape. */
function entityIndexForPointPick(entities: readonly Entity[], ref: NodeRef): number {
  if (ref.kind === 'entity') {
    // Named among the step's own shapes; reported as a position in the whole stream.
    const entity = ownEntitiesOf(entities, ref.nodeId)[ref.index];
    return entity ? entities.indexOf(entity) : -1;
  }
  if (ref.kind === 'path') return ref.index < entities.length ? ref.index : -1;
  if (ref.kind === 'origin') return entities.length > 0 ? 0 : -1;
  if (ref.kind === 'quadrant') {
    let counter = 0;
    for (let index = 0; index < entities.length; index++) {
      const count = entityQuadrants(entities[index]!).length;
      if (ref.index < counter + count) return index;
      counter += count;
    }
    return -1;
  }
  if (ref.kind === 'midline') {
    let counter = 0;
    for (let index = 0; index < entities.length; index++) {
      const count = edgesOf([entities[index]!]).length;
      if (ref.index < counter + count) return index;
      counter += count;
    }
    return -1;
  }
  if (ref.kind === 'intersection') {
    const crossing = crossingsOf(entities)[ref.index];
    if (!crossing) return -1;
    const found = entities.findIndex((entity) => (entity.name ?? entity.id) === crossing.a);
    return found;
  }
  return -1;
}

/**
 * What every constraint node asks of the graph, resolved against one evaluation.
 *
 * A constraint always wins: it is applied to the step that *owns* the geometry, so a reference
 * that placed the shape is overridden and every later step that references it follows. `A` names
 * the point that moves and `B` the point it must reach; a vertex or centre slides its own handle,
 * anything else (quadrant, midpoint, path point, crossing) carries the whole entity.
 */
function constraintAdjustments(
  graph: NodeGraph,
  layerId: string,
  previous: Adjustments,
): Adjustments {
  const out: Adjustments = new Map();
  const constraints = graph.nodes.filter(isConstraintNode);
  if (constraints.length === 0) return out;

  const { byId, resolve } = makeResolver(graph, layerId, previous);
  const add = (nodeId: string, position: number, change: Adjustment): void => {
    let changes = out.get(nodeId);
    if (!changes) {
      changes = new Map();
      out.set(nodeId, changes);
    }
    changes.set(position, change);
  };

  const tangents: TangentDemand[] = [];

  for (const node of constraints) {
    // Coincident and Tangent are separate steps now; the older combined step keeps working on
    // whichever one its `kind` names.
    const kind =
      node.type === 'tangent' ? 'tangent' : node.type === 'coincident' ? 'coincident' : constraintKindOf(node);

    // Tangent is its own constraint: the circle arrives on the wire, so only the target is picked.
    if (kind === 'tangent') {
      const target = parseRef(node.refs?.['target']);
      if (!target) continue;
      const demand = tangentDemandOf(graph, node, target, resolve, byId);
      if (demand) tangents.push(demand);
      continue;
    }

    const a = parseRef(node.refs?.['a']);
    const b = parseRef(node.refs?.['b']);
    if (!a || !b) continue;
    const source = byId.get(a.nodeId);
    if (!source) continue;

    const target = refPoint(graph, b, resolve);
    if (!target) continue;

    const stream = resolve(source);
    const spot = handleSpotAt(stream, a.kind, a.index);
    if (spot) {
      if (spot.index < stream.length) {
        add(a.nodeId, spot.index, { kind: 'handle', handle: spot.handle, point: target });
      }
      continue;
    }

    const point = refPoint(graph, a, resolve);
    if (!point) continue;
    // The stream already carries last pass's offsets, so this correction is what is still missing;
    // adding it keeps the total, which is what makes the passes settle instead of oscillating.
    const previousDelta = (position: number): Vec2 => {
      const last = previous.get(a.nodeId)?.get(position);
      return last?.kind === 'translate' ? last.delta : Vec2.of(0, 0);
    };
    const missing = target.sub(point);

    if (a.kind === 'origin') {
      // An origin names the whole step, so everything it produced travels with it.
      stream.forEach((_, position) =>
        add(a.nodeId, position, {
          kind: 'translate',
          delta: previousDelta(position).add(missing),
        }),
      );
      continue;
    }
    const position = entityIndexForPointPick(stream, a);
    if (position >= 0) {
      add(a.nodeId, position, {
        kind: 'translate',
        delta: previousDelta(position).add(missing),
      });
    }
  }

  // Tangents are answered per circle, not per constraint: two tangents on one circle have to agree
  // on a single centre, so the demands are grouped and solved together.
  const groups = new Map<string, TangentDemand[]>();
  for (const demand of tangents) {
    const key = `${demand.nodeId}#${demand.position}`;
    const list = groups.get(key);
    if (list) list.push(demand);
    else groups.set(key, [demand]);
  }
  for (const demands of groups.values()) {
    const first = demands[0]!;
    // Two tangents aimed at the *same* object (a plate picked twice) must not both take its nearest
    // side: each demand claims a different edge of it, nearest first, so the circle files into the
    // corner between them. Edges already taken by this circle are passed over.
    const claimed = new Set<string>();
    const lines: Array<readonly [Vec2, Vec2]> = [];
    const rounds: Array<{ centre: Vec2; radius: number }> = [];
    for (const demand of demands) {
      if (demand.round) {
        rounds.push(demand.round);
        continue;
      }
      let best: { edge: readonly [Vec2, Vec2]; key: string; distance: number } | null = null;
      for (const edge of demand.edges) {
        const key = tangentEdgeKey(edge);
        if (claimed.has(key)) continue;
        const distance = distancePointSegment(edge[0], edge[1], first.centre);
        if (!best || distance < best.distance) best = { edge, key, distance };
      }
      if (!best) continue;
      claimed.add(best.key);
      lines.push(best.edge);
    }
    // Edge and round demands are answered in one solve, so neither can walk the centre back off the
    // other. The centre is the only thing that moves; a coincident on the same circle is a separate
    // constraint and is not merged into this one.
    const solved = tangentCentre(first.centre, first.radius, lines, rounds);
    if (solved) add(first.nodeId, first.position, { kind: 'handle', handle: 'c', point: solved });
  }

  return out;
}

/** Same edge, same key — how one tangent group knows a side of an object is already spoken for. */
function tangentEdgeKey(edge: readonly [Vec2, Vec2]): string {
  return `${edge[0].x},${edge[0].y}|${edge[1].x},${edge[1].y}`;
}

/** One circle that has to touch one target: what the grouped tangent solve works from. */
interface TangentDemand {
  nodeId: string;
  position: number;
  centre: Vec2;
  radius: number;
  /** Every edge of the target object; the group picks which one this demand takes. */
  edges: ReadonlyArray<readonly [Vec2, Vec2]>;
  /** The round target it must touch, when the target is a circle or an arc. */
  round?: { centre: Vec2; radius: number };
}

/**
 * The circle that arrives on a tangent step, and the object it must touch. The circle is the *body*
 * of the constraint — it is on the wire — so only the thing it must touch is picked, and it is
 * picked as a whole object: the contact lands wherever it must, not on a named grip.
 */
function tangentDemandOf(
  graph: NodeGraph,
  node: GraphNode,
  target: NodeRef,
  resolve: (node: GraphNode) => Entity[],
  byId: Map<string, GraphNode>,
): TangentDemand | null {
  const lineSource = byId.get(target.nodeId);
  if (!lineSource) return null;
  const lineStream = resolve(lineSource);
  const targetIndex = pickEntityIndex(lineStream, target);
  const targetEntity = targetIndex >= 0 ? lineStream[targetIndex] : undefined;
  if (!targetEntity) return null;

    // The circle nearest the end of the stream feeding this step is the one that moves: a primitive
    // adds to the stream, so the shape you just added is the body of the constraint.
  for (const source of upstreamsOf(graph, node.id)) {
    const stream = resolve(source);
    const position = lastCircleIndex(stream);
    if (position < 0) continue;
    // Blame the step that *drew* the circle, not whichever step passed it on: two tangents chained
    // through other steps still describe one body, and have to be answered together.
    const entity = stream[position]!;
    const ownerId = entity.source && byId.has(entity.source) ? entity.source : source.id;
    const owner = byId.get(ownerId)!;
    const ownerStream = owner.id === source.id ? stream : resolve(owner);
    let ownerPosition = ownerStream.findIndex((candidate) => candidate.id === entity.id);
    if (ownerPosition < 0) ownerPosition = ownerStream.indexOf(entity);
    if (ownerPosition < 0) ownerPosition = 0;
    const found = circleOf(ownerStream[ownerPosition]);
    if (!found) continue;

    const base = {
      nodeId: ownerId,
      position: ownerPosition,
      centre: found.center,
      radius: found.radius,
    };
    // A round target: the two circles touch, so the centre moves along the line between them.
    const round = circleOf(targetEntity);
    if (round) return { ...base, edges: [], round: { centre: round.center, radius: round.radius } };

    // Every edge of the object, not just the nearest: two tangents on one plate have to take two
    // different sides of it, and only the group knows how many demands it is answering.
    const edges = edgesOf([targetEntity]);
    if (edges.length === 0) continue;
    return { ...base, edges };
  }
  return null;
}

/** The entity a pick names: a handle pick says which shape, a body pick says which index. */
function pickEntityIndex(entities: readonly Entity[], ref: NodeRef): number {
  const spot = handleSpotAt(entities, ref.kind, ref.index);
  if (spot) return spot.index;
  return entityIndexForPointPick(entities, ref);
}

/** A circle's centre and radius; the shape a tangency is solved against. */
function circleOf(entity: Entity | undefined): { center: Vec2; radius: number } | null {
  if (!entity) return null;
  if (entity.type === 'circle' || entity.type === 'arc') {
    return { center: entity.center, radius: entity.radius };
  }
  return null;
}

/**
 * Settles the constraints before the answer is drawn: each pass resolves them against the result of
 * the previous one, so a constraint whose target is itself constrained converges instead of
 * fighting. Three passes is enough for the chains a drawing produces, and equal passes stop early.
 */
function solveAdjustments(graph: NodeGraph, layerId: string): Adjustments {
  let adjustments: Adjustments = new Map();
  for (let pass = 0; pass < 3; pass++) {
    const next = constraintAdjustments(graph, layerId, adjustments);
    if (sameAdjustments(adjustments, next)) return next;
    adjustments = next;
  }
  return adjustments;
}

/** The graph's own fields and wiring — what the constraint solve depends on. */
function graphSignature(graph: NodeGraph): string {
  const wire = graph.edges.map((edge) => `${edge.from}>${edge.to}`).join(',');
  return `${wire}||${graph.nodes
    .map((node) => `${node.id}:${stepSignature(node, '', undefined)}`)
    .join('||')}`;
}

/** The settled constraints, remembered until any step or wire changes. */
const solveMemos = new WeakMap<
  NodeGraph,
  { layerId: string; signature: string; adjustments: Adjustments }
>();

function solvedAdjustments(graph: NodeGraph, layerId: string): Adjustments {
  if (!graph.nodes.some(isConstraintNode)) return new Map();
  const signature = graphSignature(graph);
  const memo = solveMemos.get(graph);
  if (memo && memo.layerId === layerId && memo.signature === signature) return memo.adjustments;
  const adjustments = solveAdjustments(graph, layerId);
  solveMemos.set(graph, { layerId, signature, adjustments });
  return adjustments;
}

/** Pull-based evaluation of the graph feeding the output node. Cycles are ignored. */
export function evaluateGraph(graph: NodeGraph, layerId = '0'): Entity[] {
  const { resolve } = makeResolver(graph, layerId, solvedAdjustments(graph, layerId));
  const output = findOutputNode(graph);
  return output ? resolve(output) : [];
}

/** Entities a single step produces (used by ref pickers and node readouts). */
export function evaluateNode(graph: NodeGraph, nodeId: string, layerId = '0'): Entity[] {
  const { byId, resolve } = makeResolver(graph, layerId, solvedAdjustments(graph, layerId));
  const target = byId.get(nodeId);
  return target ? resolve(target) : [];
}

/** The steps of the graph, oldest first — the modelling history of the drawing. */
export function describeHistory(graph: NodeGraph): Array<{ id: string; label: string }> {
  return graph.nodes.map((node) => {
    const def = NODE_DEFS[node.type];
    if (isValueNode(node)) {
      const value = evaluateValue(graph, node.id) ?? 0;
      return { id: node.id, label: `${def.title} = ${round(value)}` };
    }
    const details = activeParams(node)
      .map((param) => {
        const value = node.params[param.id] ?? param.value;
        if (param.choices) {
          const choice = param.choices.find((item) => item.value === Math.round(value));
          return `${param.label} ${choice?.label ?? value}`;
        }
        return `${param.label} ${Math.round(value * 100) / 100}`;
      })
      .join(', ');
    const trimmed = node.hidden?.length ? `${node.hidden.length} edges trimmed` : '';
    const picked = node.picks?.length ? `${node.picks.length} picked` : '';
    const extra = [details, trimmed, picked].filter(Boolean).join(', ');
    return { id: node.id, label: `${def.title}${extra ? ` (${extra})` : ''}` };
  });
}

/** True when `from` already depends on `to`, so linking would create a cycle. */
export function wouldCreateCycle(graph: NodeGraph, from: string, to: string): boolean {
  if (from === to) return true;
  const seen = new Set<string>();
  const stack = [from];
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (current === to) return true;
    if (seen.has(current)) continue;
    seen.add(current);
    // Every input counts, not just the stream: a second-input wire can close a loop too.
    for (const edge of graph.edges) if (edge.to === current) stack.push(edge.from);
  }
  return false;
}

/** Wires `from` into one input of `to` — the stream input, or the socket named `slot`. */
export function linkNodes(graph: NodeGraph, from: string, to: string, slot = ''): boolean {
  if (wouldCreateCycle(graph, from, to)) return false;
  graph.edges = graph.edges.filter(
    (edge) => edge.to !== to || (edge.slot ?? '') !== slot,
  );
  graph.edges.push(slot ? { from, to, slot } : { from, to });
  return true;
}

/** Removes the wire feeding one input of `nodeId` (the stream input by default). */
export function unlinkNode(graph: NodeGraph, to: string, slot = ''): void {
  graph.edges = graph.edges.filter(
    (edge) => edge.to !== to || (edge.slot ?? '') !== slot,
  );
}

export function removeNode(graph: NodeGraph, id: string): void {
  graph.nodes = graph.nodes.filter((node) => node.id !== id);
  graph.edges = graph.edges.filter((edge) => edge.from !== id && edge.to !== id);
  for (const node of graph.nodes) {
    // A deleted maths step releases every operand it was driving.
    if (node.values) {
      for (const [paramId, sourceId] of Object.entries(node.values)) {
        if (sourceId === id) delete node.values[paramId];
      }
      if (Object.keys(node.values).length === 0) delete node.values;
    }
    if (!node.refs) continue;
    for (const [key, value] of Object.entries(node.refs)) {
      if (parseRef(value)?.nodeId === id) delete node.refs[key];
    }
  }
}
